import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "./mcSessionDetail.css";
import { archiveSessionRecord, ensureCanonicalCountLedger, findSessionSeedById, loadArchivedSessionRecords, loadPreparedRowsFromSessionSeed, loadSessionDetailSnapshotForCompare, resolveFlagConfig, resolveUnitRuleForPreparedRow, loadEngineState, upsertSessionRecord, loadSessionRecords } from "../utils/mcEngineData";
import { activeSections, formatSectionLabel, normalizeSectionId } from "../utils/mcSections";
import { mcGetItem, mcSetItem, mcSetCachedItem, mcGetCloudItem, mcSetCloudItem, mcSetCloudItemImmediate, mcSubscribeCloudItem, mcTransitionCountLedger, getMcActiveOrgId } from "../utils/mcCloudSync";
import { countTouchedRows, drainLiveCountSaveQueue, enqueueLiveCountSave, subscribeLiveCountSaveQueue } from "../utils/mcLiveCountSaveQueue";
import { exportMorpheusOrderSheetPdf, exportSessionCountPdf } from "./mcCountPdfExport";
import { buildProfileDisplayName, canFinalizeCount, filterRecordsForProfile, getActiveStoreScope, isAdminProfile, normalizeStoreValue, useAdminStoreView, useCurrentMcProfile } from "../utils/profileAccess";
import { appendResponseEvent, buildResponseEventFromSession, recordResponseCorrection } from "../utils/mcResponseEvents";
import { getNotificationsForEvent } from "../utils/mcNotifications";
import { installOperationalDateRefreshReset } from "../utils/mcSessionDateMemory";
import { activateMcTrafficProbeSession, getMcTrafficProbeSnapshot, isMcTrafficProbeEnabled, recordMcTrafficEdit, resetMcTrafficProbe } from "../utils/mcTrafficProbe";
import { hasZeroCostVarianceFlagBoundary, resolveCostVarianceFlagState } from "../utils/mcFlagging";

const fallbackProducts = [
  { id: "P-001", product: "Blue Dream Indoor", category: "flower", cost: 75, systemSoh: 12, sku: "10024" },
  { id: "P-002", product: "Gelato Indoor", category: "flower", cost: 80, systemSoh: 8, sku: "10080" },
  { id: "P-003", product: "Pre Roll AAA", category: "pre-rolls", cost: 35, systemSoh: 22, sku: "10476" },
];

const storageKeyFor = (sessionId) => `mc-session-engine:${sessionId}`;
const checkpointKeyFor = (sessionId) => `mc-session-checkpoint:${sessionId}`;
const countLedgerKeyFor = (sessionId) => `mc-count-ledger:${sessionId}`;
const finalSnapshotKeyFor = (sessionId) => `mc-final-count-snapshot:${sessionId}`;
// Immutable authority written when a live count is completed. Normal autosave
// traffic may continue to arrive out of order from another browser, but it must
// never be able to replace the frozen completed row values.
const completedSnapshotKeyFor = (sessionId) => `mc-session-completed:${sessionId}`;
const correctionDraftKeyFor = (sessionId) => `mc-session-correction-draft:${sessionId}`;
// Tiny synchronous per-field recovery journal. This is deliberately NOT an
// `mc-` key, so organisation cache hydration/Access Layer cleanup cannot erase
// keystrokes that have not reached Firestore yet. The canonical ledger remains
// the source of truth; this journal only bridges an interrupted autosave.
const liveSaveOutboxKeyFor = (sessionId) => `__mc-live-count-outbox:${sessionId}`;

// MC489 — full Count Ledger cloud checkpoints are deliberately less frequent than
// keystrokes. Every edit is still crash-protected immediately by the tiny local
// field outbox; Firestore receives a full 80-90 KB ledger after a small batch of
// edits or after the maximum checkpoint window, whichever comes first. Explicit
// lifecycle actions (Save now / leave / hide / Complete) still force a snapshot.
const LIVE_LEDGER_CHECKPOINT_MAX_MS = 8000;
const LIVE_LEDGER_CHECKPOINT_EDIT_BATCH = 8;
const LIVE_LEDGER_CHECKPOINT_MIN_DELAY_MS = 80;

// CI4 — completion comparison intelligence. Comparison discovery is deliberately
// on-demand at the moment of completion: no background listener and no archive scan.
// The previous comparable count is the latest FINALIZED historical record from
// the same site + same section, regardless of cycle/date/product scope.
function comparisonSiteKey(record = {}) {
  return normalizeStoreValue(
    record?.storeId || record?.siteId || record?.storeName || record?.store || record?.siteName || record?.meta?.storeName || record?.meta?.store || ""
  );
}

function isFinalizedComparisonAuthority(record = {}) {
  const meta = record?.meta || {};
  const lifecycle = String(record?.lifecycleState || meta?.lifecycleState || "").trim().toLowerCase();
  const status = String(record?.status || record?.sessionStatus || meta?.status || "").trim().toLowerCase();
  const reason = String(record?.archiveReason || record?.reason || "").trim().toLowerCase();
  return Boolean(
    record?.finalizedAt
    || meta?.finalizedAt
    || lifecycle === "finalized"
    || status === "finalized"
    || reason.startsWith("finalized")
  );
}

function comparisonSectionKey(record = {}) {
  const fullSite = record?.fullSite === true || String(record?.countScope || record?.sessionType || "").toLowerCase().includes("full");
  if (fullSite) return normalizeSectionId("Full Count");
  return normalizeSectionId(record?.sectionId || record?.sectionName || record?.meta?.sectionId || record?.meta?.sectionName || "");
}

function comparisonRecordTime(record = {}) {
  const value = record?.sectionCompletedAt || record?.completedAt || record?.archivedAt || record?.updatedAt || record?.lastEditedAt || record?.createdAt || record?.startedAt || 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function comparisonExposure(record = {}) {
  const summary = record?.sessionSummary || record?.meta?.sessionSummary || {};
  return Number(summary?.exposure ?? summary?.costExposure ?? summary?.finalExposure ?? record?.totalExposure ?? record?.costExposure ?? record?.finalExposure ?? record?.exposure ?? 0);
}

function comparisonFlags(record = {}) {
  const summary = record?.sessionSummary || record?.meta?.sessionSummary || {};
  return Number(summary?.flaggedCount ?? summary?.flaggedItems ?? summary?.flaggedProducts ?? record?.flaggedCount ?? record?.flaggedItems ?? record?.flaggedProducts ?? 0);
}

function comparisonActor(record = {}) {
  return record?.countedBy || record?.completedBy || record?.lastEditedBy || record?.startedBy || record?.userName || record?.user || "Previous counter";
}

function comparisonCycle(record = {}) {
  return record?.cycleName || record?.meta?.cycleName || "Previous cycle";
}

function readLiveSaveOutbox(sessionId) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem(liveSaveOutboxKeyFor(sessionId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.sessionId === sessionId && parsed.fields && typeof parsed.fields === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeLiveSaveOutboxField(sessionId, lineId, field, value, version, liveRevision = 0) {
  if (typeof window === "undefined" || !sessionId || !lineId || !field) return;
  try {
    const current = readLiveSaveOutbox(sessionId) || { sessionId, fields: {} };
    current.fields[`${String(lineId)}:${String(field)}`] = {
      lineId: String(lineId), field: String(field), value, version: version || null,
    };
    current.liveRevision = Math.max(Number(current.liveRevision || 0), Number(liveRevision || 0));
    current.updatedAt = new Date().toISOString();
    window.localStorage?.setItem(liveSaveOutboxKeyFor(sessionId), JSON.stringify(current));
  } catch (err) {
    console.warn("MC live save outbox write failed", err);
  }
}

function clearLiveSaveOutbox(sessionId) {
  if (typeof window === "undefined") return;
  try { window.localStorage?.removeItem(liveSaveOutboxKeyFor(sessionId)); } catch (_) {}
}

function applyLiveSaveOutbox(payload, outbox) {
  if (!payload || !Array.isArray(payload.lines) || !outbox?.fields) return payload;
  const fields = Object.values(outbox.fields || {});
  if (!fields.length) return payload;
  const byLine = new Map();
  fields.forEach((entry) => {
    if (!entry?.lineId || !entry?.field) return;
    if (!byLine.has(entry.lineId)) byLine.set(entry.lineId, []);
    byLine.get(entry.lineId).push(entry);
  });
  const nextLines = payload.lines.map((line) => {
    const edits = byLine.get(String(line.id));
    if (!edits?.length) return line;
    const next = { ...line, _mcFieldVersions: { ...(line?._mcFieldVersions || {}) } };
    edits.forEach((entry) => {
      next[entry.field] = entry.value;
      if (entry.version) next._mcFieldVersions[entry.field] = entry.version;
    });
    return next;
  });
  const now = outbox.updatedAt || new Date().toISOString();
  return {
    ...payload,
    meta: { ...(payload.meta || {}), liveRevision: Math.max(Number(payload?.meta?.liveRevision || 0), Number(outbox.liveRevision || 0)), lastEditedAt: now },
    lines: nextLines,
    savedAt: now,
  };
}
const ORDER_SHEETS_KEY = "mc-morpheus-order-sheets";

function completedPayloadTouchedCount(payload) {
  return (Array.isArray(payload?.lines) ? payload.lines : []).filter((line) =>
    String(line?.counted ?? "").trim() || String(line?.waste ?? "").trim() || String(line?.note ?? "").trim()
  ).length;
}

function completedPayloadIsTerminal(payload) {
  const status = String(payload?.meta?.status || "").trim().toLowerCase();
  return Boolean(payload?.meta?.sectionCompletedAt || payload?.meta?.completedAt || ["section complete", "completed", "complete"].includes(status));
}

// Completion metadata and row values are separate authorities. A late/partial terminal
// envelope must never erase a fuller pre-completion checkpoint. Keep the terminal
// lifecycle metadata, but always pair it with the fullest available row set.
function composeCompletedAuthority(...payloads) {
  const valid = payloads.filter((payload) => payload && Array.isArray(payload?.lines) && payload.lines.length);
  if (!valid.length) return null;
  const terminal = valid
    .filter(completedPayloadIsTerminal)
    .sort((a, b) => {
      const summaryDelta = Number(b?.meta?.sessionSummary?.completedLines || b?.meta?.completedLines || 0) - Number(a?.meta?.sessionSummary?.completedLines || a?.meta?.completedLines || 0);
      if (summaryDelta) return summaryDelta;
      return (Date.parse(b?.savedAt || b?.meta?.lastEditedAt || 0) || 0) - (Date.parse(a?.savedAt || a?.meta?.lastEditedAt || 0) || 0);
    })[0] || null;
  const fullestRows = [...valid].sort((a, b) => {
    const touchedDelta = completedPayloadTouchedCount(b) - completedPayloadTouchedCount(a);
    if (touchedDelta) return touchedDelta;
    const summaryDelta = Number(b?.meta?.sessionSummary?.completedLines || b?.meta?.completedLines || 0) - Number(a?.meta?.sessionSummary?.completedLines || a?.meta?.completedLines || 0);
    if (summaryDelta) return summaryDelta;
    return (Date.parse(b?.savedAt || b?.meta?.lastEditedAt || 0) || 0) - (Date.parse(a?.savedAt || a?.meta?.lastEditedAt || 0) || 0);
  })[0];
  if (!terminal) return fullestRows;
  if (!fullestRows || fullestRows === terminal) return terminal;
  return {
    ...fullestRows,
    meta: { ...fullestRows.meta, ...terminal.meta },
    lines: fullestRows.lines,
    savedAt: terminal.savedAt || fullestRows.savedAt,
  };
}

function roundTo2(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/,/g, ".") || 0);
  return Math.round(n * 100) / 100;
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "—";
  const rounded = roundTo2(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatCurrency(value) {
  const amount = roundTo2(value || 0);
  const abs = Math.abs(amount);
  const body = abs.toLocaleString(undefined, {
    minimumFractionDigits: abs % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  if (amount < 0) return `-R${body}`;
  return `R${body}`;
}


function numericUnitValue(value, fallback = 1) {
  const parsed = Number(String(value ?? "").replace(/,/g, "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function resolveLineUnitCost(line = {}, meta = {}, activeStoreScope = "ALL", resolvedEngineConfig = null) {
  // MC485 — live typing must never rebuild/hydrate the Morpheus engine once per
  // row. Callers on the hot render/save path pass the already-resolved site config
  // so Unit Intelligence calculations stay identical without 94+ loadEngineState()
  // calls for every digit or note character. Lifecycle/recovery callers can omit it
  // and retain the legacy self-resolving behaviour.
  const config = resolvedEngineConfig && typeof resolvedEngineConfig === "object"
    ? resolvedEngineConfig
    : (() => {
        const scope = meta?.storeKey || meta?.storeName || meta?.store || activeStoreScope || "ALL";
        const engine = loadEngineState(scope) || loadEngineState("ALL") || {};
        return engine?.config || {};
      })();
  const sourceCost = Number(line.sourceCost ?? line.importedCost ?? line.originalCost ?? line.cost ?? 0);
  const rule = resolveUnitRuleForPreparedRow(line.product || "", line.category || "", config);
  const priceUnitSize = numericUnitValue(rule?.priceUnitSize, numericUnitValue(line.priceUnitSize, 1));
  const effectiveCost = rule?.priceBasis === "base"
    ? sourceCost
    : sourceCost / priceUnitSize;
  const systemUnitSize = numericUnitValue(rule?.systemUnitSize, numericUnitValue(line.systemUnitSize, 1));
  const priceBasis = rule?.priceBasis || line.priceBasis || "count";
  const hasUnitConversion = systemUnitSize !== 1 || priceUnitSize !== 1 || priceBasis === "base" || rule?.autoDetected === true || line.autoDetected === true;
  return {
    cost: roundTo2(effectiveCost),
    sourceCost,
    countUnit: rule?.countUnit || line.countUnit || "Unit",
    baseUnit: rule?.baseUnit || line.baseUnit || "Unit",
    systemUnitSize,
    priceUnitSize,
    priceBasis,
    autoDetected: rule?.autoDetected === true || line.autoDetected === true,
    detectedSize: rule?.detectedSize || line.detectedSize || null,
    detectedUnit: rule?.detectedUnit || line.detectedUnit || null,
    conversionLabel: hasUnitConversion
      ? `${rule?.countUnit || line.countUnit || "Unit"} → ${systemUnitSize} ${rule?.baseUnit || line.baseUnit || "Unit"}; price ${priceBasis === "base" ? `per ${rule?.baseUnit || line.baseUnit || "Unit"}` : `per ${priceUnitSize} ${rule?.baseUnit || line.baseUnit || "Unit"}`}`
      : "",
  };
}

function formatRelativeLastSaved(lastSavedAt) {
  if (!lastSavedAt) return "Not saved yet";
  return `Last saved: ${new Date(lastSavedAt).toLocaleString()}`;
}

function formatTrafficBytes(value) {
  const bytes = Math.max(0, Number(value || 0));
  if (bytes < 1024) return `${bytes.toFixed(0)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 2 : 1)} MB`;
}

function trafficKeyLabel(key = "", sessionId = "") {
  const clean = String(key || "");
  if (clean === "mc-session-list") return "Session list";
  if (/^mc-count-ledger:/i.test(clean)) return "Count Ledger";
  if (/^mc-session-engine[:\-]/i.test(clean)) return "Session engine";
  if (/^mc-session-checkpoint:/i.test(clean)) return "Session checkpoint";
  if (/^mc-session-completed:/i.test(clean)) return "Completed mirror";
  if (/^mc-final-count-snapshot:/i.test(clean)) return "Final snapshot";
  if (/^mc-archive-session:/i.test(clean)) return "Archive session";
  return clean.replace(String(sessionId || ""), "…") || "Unknown key";
}


function normalizeOwnershipCategory(value = "") {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}


function sessionCategoryScopeLabel(meta = {}) {
  const groupNames = Array.isArray(meta?.countGroupNames) ? meta.countGroupNames : [];
  const categoryNames = Array.isArray(meta?.categoryNames) ? meta.categoryNames : [];
  const names = groupNames.length ? groupNames : categoryNames;
  return names.length ? names.join(' • ') : (meta?.categoryScopeLabel || 'All Count Groups');
}

function resolveSectionFromCountLines(lines = [], meta = {}) {
  const existingId = normalizeSectionId(meta?.sectionId || meta?.sectionName || "");
  const existingName = String(meta?.sectionName || "").trim();
  if (existingName && existingId !== "section") {
    return { sectionId: existingId, sectionName: existingName };
  }

  const engine = loadEngineState(meta?.storeKey || meta?.storeName || meta?.store || "") || loadEngineState() || {};
  const ownership = engine?.config?.categoryOwnership || {};
  const sections = activeSections();
  const sectionById = new Map(sections.map((section) => [normalizeSectionId(section?.sectionId || section?.id || section?.name), section]));
  const seen = new Set();

  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const direct = normalizeSectionId(line?.sectionId || line?.sectionName || "");
    if (direct && direct !== "section") {
      seen.add(direct);
      return;
    }
    const categoryKey = normalizeOwnershipCategory(line?.category || line?.output || line?.product_category || "");
    const owner = normalizeSectionId(ownership?.[categoryKey] || ownership?.[line?.category] || ownership?.[line?.output] || "");
    if (owner && owner !== "section") seen.add(owner);
  });

  if (seen.size !== 1) return { sectionId: "", sectionName: "" };
  const sectionId = Array.from(seen)[0];
  const section = sectionById.get(sectionId);
  return { sectionId, sectionName: section?.name || formatSectionLabel(sectionId) };
}

function evaluateCountExpression(rawValue) {
  const source = String(rawValue ?? "").trim();
  if (!source) return "";

  const normalized = source.replace(/,/g, ".").replace(/\s+/g, "");
  if (!/^[0-9+\-*/().]+$/.test(normalized)) return source;
  if (normalized.includes("**") || normalized.includes("//")) return source;

  try {
    const result = Function(`"use strict"; return (${normalized});`)();
    if (!Number.isFinite(result)) return source;
    const rounded = roundTo2(result);
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
  } catch {
    return source;
  }
}

function liveCountNumber(rawValue) {
  const source = String(rawValue ?? "").trim();
  if (!source) return 0;

  const normalized = source.replace(/,/g, ".").replace(/\s+/g, "");
  if (!/^[0-9+\-*/().]+$/.test(normalized) || normalized.includes("**") || normalized.includes("//")) {
    const fallback = Number(source.replace(/,/g, "."));
    return Number.isFinite(fallback) ? roundTo2(fallback) : 0;
  }

  const safeExpression = normalized.replace(/[+\-*/.]+$/g, "");
  if (!safeExpression) return 0;

  try {
    const result = Function('"use strict"; return (' + safeExpression + ');')();
    return Number.isFinite(result) ? roundTo2(result) : 0;
  } catch {
    const fallback = Number(safeExpression);
    return Number.isFinite(fallback) ? roundTo2(fallback) : 0;
  }
}


function buildLiveSessionSummary(lines = [], meta = {}, activeStoreScope = "ALL", flagConfig = {}, resolvedEngineConfig = null) {
  let completedLines = 0;
  let flaggedCount = 0;
  let exposure = 0;
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const touched = line?.counted !== "" || line?.waste !== "" || Boolean(String(line?.note || "").trim());
    if (!touched) return;
    completedLines += 1;
    const countedValue = liveCountNumber(line?.counted);
    const wasteValue = liveCountNumber(line?.waste);
    const unitCost = resolveLineUnitCost(line, meta, activeStoreScope, resolvedEngineConfig);
    const variance = roundTo2(Number(countedValue || 0) + Number(wasteValue || 0) - Number(line?.systemSoh || 0));
    const costVariance = roundTo2(Number(variance || 0) * Number(unitCost?.cost || 0));
    exposure += Number(costVariance || 0);
    const noteFlagged = Boolean(String(line?.note || "").trim());
    const { negativeFlagged, positiveFlagged } = resolveCostVarianceFlagState(costVariance, flagConfig);
    if (noteFlagged || negativeFlagged || positiveFlagged) flaggedCount += 1;
  });
  return {
    totalLines: Array.isArray(lines) ? lines.length : 0,
    completedLines,
    flaggedCount,
    costExposure: roundTo2(exposure),
    exposure: roundTo2(exposure),
  };
}


function loadOrderSheets() {
  try {
    const parsed = JSON.parse(mcGetItem(ORDER_SHEETS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveOrderSheets(sheets) {
  mcSetItem(ORDER_SHEETS_KEY, JSON.stringify(Array.isArray(sheets) ? sheets : []));
}

function profileKey(profile) {
  return String(profile?.id || profile?.uid || profile?.email || profile?.name || "user");
}

function normalizeCategoryText(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}


function mosOrderNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const n = Number(raw.replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
}

function filterMosRowsForExport(rows = [], options = {}) {
  const hideZeroOrders = Boolean(options?.hideZeroOrderItemsInMosPdf);
  const sourceRows = Array.isArray(rows) ? rows : [];
  if (!hideZeroOrders) return sourceRows;
  return sourceRows.filter((row) => mosOrderNumber(row?.order) > 0);
}

function resolveCountGroupName(category, groups = []) {
  const normalized = normalizeCategoryText(category);
  const match = (groups || []).find((group) =>
    (group?.outputs || []).some((output) => normalizeCategoryText(output) === normalized)
  );
  return match?.name || "Other";
}

function buildCategoryRank(categoryOrder = []) {
  const rank = {};
  (categoryOrder || []).forEach((category, index) => {
    rank[normalizeCategoryText(category)] = index;
  });
  return rank;
}

function sortRowsByCategoryOrder(rows = [], categoryRank = {}) {
  return [...rows].sort((a, b) => {
    const ar = Object.prototype.hasOwnProperty.call(categoryRank, normalizeCategoryText(a.category)) ? categoryRank[normalizeCategoryText(a.category)] : 99999;
    const br = Object.prototype.hasOwnProperty.call(categoryRank, normalizeCategoryText(b.category)) ? categoryRank[normalizeCategoryText(b.category)] : 99999;
    if (ar !== br) return ar - br;
    return String(a.product || "").localeCompare(String(b.product || ""));
  });
}

function groupRank(group, categoryRank = {}) {
  const ranks = (group?.outputs || [])
    .map((output) => categoryRank[normalizeCategoryText(output)])
    .filter((value) => Number.isFinite(value));
  return ranks.length ? Math.min(...ranks) : 99999;
}

export default function MorpheusCountSessionDetail() {
  useEffect(() => installOperationalDateRefreshReset(), []);

  const params = useParams();
  const locationForSessionFallback = useLocation();
  const sessionId = params?.sessionId
    || decodeURIComponent(String(locationForSessionFallback?.pathname || "").split("/morpheus-count/session/")[1] || "").split("/")[0]
    || "MC-NEW-001";
  const navigate = useNavigate();
  const location = useLocation();
  const currentProfile = useCurrentMcProfile();
  const adminStoreView = useAdminStoreView();
  const activeStoreScope = getActiveStoreScope(currentProfile);
  const currentProfileRef = useRef(currentProfile);
  const activeStoreScopeRef = useRef(activeStoreScope);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const isArchivedView = new URLSearchParams(location.search).get("archived") === "1";
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [meta, setMeta] = useState(null);
  const [lines, setLines] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSavedAtRef = useRef(null);
  const [saveState, setSaveState] = useState("saved");
  const [saveProgress, setSaveProgress] = useState({ savedCount: 0, targetCount: 0, totalCount: 0, pending: false });
  // MC486 — localhost/development-only traffic inspector. The probe observes the
  // existing Live Count Firestore path; it never creates a cloud read/write itself.
  const trafficProbeEnabled = useMemo(() => isMcTrafficProbeEnabled(), []);
  const [trafficOpen, setTrafficOpen] = useState(false);
  const [trafficStats, setTrafficStats] = useState(() => getMcTrafficProbeSnapshot(sessionId));
  // Refreshing directly on a live-count route can mount this page before the
  // Morpheus cloud layer has resolved the active organisation. In that state
  // mcGetCloudItem/mcSubscribeCloudItem intentionally return no data, and the
  // old code permanently fell back to the original prepared 0/N seed. Bump a
  // small epoch whenever cloud authority becomes available so hydration and the
  // live Firestore subscription are rebuilt against the real session detail.
  const [cloudAuthorityEpoch, setCloudAuthorityEpoch] = useState(0);
  const [search, setSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");
  const [heldUncountedIds, setHeldUncountedIds] = useState(() => new Set());
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderSheets, setOrderSheets] = useState([]);
  const [orderDraft, setOrderDraft] = useState([]);
  const [selectedOrderSheet, setSelectedOrderSheet] = useState(null);
  const [completionResponse, setCompletionResponse] = useState(null);
  const [completionError, setCompletionError] = useState("");
  const [completionComparison, setCompletionComparison] = useState(null);
  const [completionComparisonLoading, setCompletionComparisonLoading] = useState(false);
  const [correctionInfoOpen, setCorrectionInfoOpen] = useState(false);
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionOtherReason, setCorrectionOtherReason] = useState("");
  const [correctionStartedAt, setCorrectionStartedAt] = useState(null);
  const [originalCorrectionLines, setOriginalCorrectionLines] = useState([]);
  const [correctionSubmitOpen, setCorrectionSubmitOpen] = useState(false);
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionResult, setCorrectionResult] = useState(null);
  const [archiveDetailTab, setArchiveDetailTab] = useState("overview");
  const saveTimeoutRef = useRef(null);
  const hasHydratedRef = useRef(false);
  // A prepared seed is only a recovery scaffold, never authoritative live state.
  // If a refresh temporarily cannot read Firestore, we must not let the blank
  // seed overwrite a real in-progress count or its Sessions summary.
  const detailAuthorityRef = useRef("loading");
  const hasUserEditedRef = useRef(false);
  // MC489 cloud checkpoint cadence. These refs are intentionally separate from
  // the durable queue: edits can continue locally while an older checkpoint is
  // in flight, without allowing that older acknowledgement to clear newer edits.
  const checkpointWindowStartedAtRef = useRef(0);
  const editsSinceCloudCheckpointRef = useRef(0);
  const lastQueuedEditSequenceRef = useRef(0);
  // Protect optimistic typing from Firestore echo/stale snapshots. The local
  // keystroke remains authoritative until its exact cloud write has settled.
  const localEditSequenceRef = useRef(0);
  // Monotonic edit revision survives long pauses and stale whole-session rewrites.
  // Every actual counter edit increments this value; the same revision is stored
  // in both the primary live detail and an independent durable checkpoint.
  const liveRevisionRef = useRef(0);
  // Stable per-tab writer identity used by the canonical Count Ledger. Every
  // editable field carries its own version so an older async Firestore write can
  // never replace a newer value from the same or another browser.
  const ledgerWriterIdRef = useRef(`mcw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`);
  const ledgerFieldSequenceRef = useRef(0);
  const latestLocalSnapshotAtRef = useRef(0);
  // Keep this browser visually authoritative for a short grace period after each
  // keystroke. Firestore can legitimately echo an older committed snapshot while
  // the current debounce/write is still in flight; applying that echo causes the
  // input to blank/revert for a split second before the newest snapshot arrives.
  const localTypingAuthorityUntilRef = useRef(0);
  const fieldInputRefs = useRef({ counted: {}, waste: {}, note: {} });
  // Per-field optimistic authority. A Firestore snapshot is allowed to update the
  // rest of the count immediately, but it may never blank/revert a field this
  // browser has edited until Firestore has echoed that exact value back. This is
  // stronger than a time-based grace window and removes the visible keystroke
  // disappear/reappear race during fast counting.
  const localDirtyFieldsRef = useRef(new Map());
  // Once this browser starts counting, the visible form becomes local-first for
  // the lifetime of this mounted session. Firestore continues to receive every
  // autosave and other browsers continue to receive live updates, but cloud
  // echoes are never allowed to repaint the counter's controlled inputs mid-count.
  // A refresh/remount hydrates the latest durable cloud snapshot normally.
  const localSessionUiAuthorityRef = useRef(false);
  const latestLinesRef = useRef([]);
  const latestMetaRef = useRef(null);
  const orderInputRefs = useRef({});

  // MC485 — resolve the site engine once for the mounted count/site instead of
  // inside every row calculation. `meta` itself changes on each keystroke because
  // lastEditedAt/liveRevision change, so depend only on the scope identity fields.
  const unitEngineState = useMemo(() => {
    const scope = meta?.storeKey || meta?.storeName || meta?.store || activeStoreScope || "ALL";
    return loadEngineState(scope) || loadEngineState("ALL") || {};
  }, [meta?.storeKey, meta?.storeName, meta?.store, activeStoreScope]);
  const unitEngineConfig = useMemo(() => unitEngineState?.config || {}, [unitEngineState]);
  const flagConfig = useMemo(() => resolveFlagConfig(meta?.store || activeStoreScope || "ALL"), [meta?.store, activeStoreScope]);

  useEffect(() => {
    if (!trafficProbeEnabled || isArchivedView || !sessionId) return undefined;
    const deactivate = activateMcTrafficProbeSession(sessionId);
    setTrafficStats(getMcTrafficProbeSnapshot(sessionId));
    return () => deactivate();
  }, [trafficProbeEnabled, isArchivedView, sessionId]);

  useEffect(() => {
    if (!trafficProbeEnabled || isArchivedView || !sessionId || !trafficOpen) return undefined;
    // UI refresh only while the panel is open; this polls in-memory counters, not
    // Firestore, and avoids adding background React renders to the counting path.
    setTrafficStats(getMcTrafficProbeSnapshot(sessionId));
    const timer = window.setInterval(() => {
      setTrafficStats(getMcTrafficProbeSnapshot(sessionId));
    }, 700);
    return () => window.clearInterval(timer);
  }, [trafficProbeEnabled, isArchivedView, sessionId, trafficOpen]);


  useEffect(() => {
    currentProfileRef.current = currentProfile;
    activeStoreScopeRef.current = activeStoreScope;
  }, [currentProfile, activeStoreScope]);

  useEffect(() => {
    latestLinesRef.current = lines;
  }, [lines]);

  useEffect(() => {
    latestMetaRef.current = meta;
  }, [meta]);

  useEffect(() => {
    if (typeof window === "undefined" || isArchivedView) return undefined;
    const flushLatestDraft = () => {
      if (!hasUserEditedRef.current || !latestMetaRef.current || !latestLinesRef.current?.length) return;
      const now = new Date().toISOString();
      const flushMeta = latestMetaRef.current || {};
      const payload = {
        meta: {
          ...flushMeta,
          liveRevision: liveRevisionRef.current,
          lastEditedAt: now,
          // Preserve MC483 live Sessions exposure even when navigation/page-hide
          // flushes before the normal debounced autosave has fired.
          liveSessionSummary: buildLiveSessionSummary(latestLinesRef.current, flushMeta, activeStoreScope, flagConfig, unitEngineConfig),
        },
        lines: latestLinesRef.current,
        savedAt: now,
      };
      const raw = JSON.stringify(payload);
      // Synchronous/local best effort first. The outbox written on each keystroke
      // is the crash-safe fallback if the network request is aborted by refresh.
      mcSetCachedItem(countLedgerKeyFor(sessionId), raw);
      mcSetCachedItem(storageKeyFor(sessionId), raw);
      mcSetCachedItem(checkpointKeyFor(sessionId), raw);
      // This forced lifecycle flush contains every edit currently visible in
      // this browser, so only its acknowledgement may clear the local outbox.
      lastQueuedEditSequenceRef.current = localEditSequenceRef.current;
      editsSinceCloudCheckpointRef.current = 0;
      checkpointWindowStartedAtRef.current = 0;
      enqueueLiveCountSave({
        sessionId,
        cloudKey: countLedgerKeyFor(sessionId),
        raw,
        touchedCount: countTouchedRows(latestLinesRef.current),
        totalCount: latestLinesRef.current.length,
        orgId: getMcActiveOrgId(),
      });
    };
    const onPageHide = () => flushLatestDraft();
    const onVisibility = () => { if (document.visibilityState === "hidden") flushLatestDraft(); };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibility);
      // SPA route changes (Back to Sessions / Access Layer) do not fire pagehide.
      // Fire the same best-effort flush on unmount; the outbox guarantees recovery
      // even if the operator refreshes before Firestore acknowledges it.
      flushLatestDraft();
    };
  }, [sessionId, isArchivedView, activeStoreScope, flagConfig, unitEngineConfig]);

  useEffect(() => {
    if (isArchivedView || !sessionId) return undefined;
    return subscribeLiveCountSaveQueue(sessionId, (status) => {
      // An older checkpoint can finish while newer edits are intentionally waiting
      // for the next MC489 batch/window. Never let that older acknowledgement say
      // "saved" or clear the tiny recovery outbox for edits it did not contain.
      const latestLocalEditIsQueued = lastQueuedEditSequenceRef.current >= localEditSequenceRef.current;
      const localChangesStillWaiting = hasUserEditedRef.current && !latestLocalEditIsQueued;
      setSaveProgress((current) => ({
        savedCount: Math.max(Number(current.savedCount || 0), Number(status.savedCount || 0)),
        targetCount: localChangesStillWaiting
          ? Math.max(Number(current.targetCount || 0), countTouchedRows(latestLinesRef.current))
          : Number(status.targetCount || current.targetCount || 0),
        totalCount: Number(status.totalCount || current.totalCount || latestLinesRef.current.length || lines.length || 0),
        pending: Boolean(localChangesStillWaiting || status.pending || status.state === "saving"),
      }));
      if (status.state === "saved" && !status.pending && latestLocalEditIsQueued) {
        setSaveState("saved");
        hasUserEditedRef.current = false;
        clearLiveSaveOutbox(sessionId);
      } else if (localChangesStillWaiting || status.state === "saving" || status.pending) {
        setSaveState("saving");
      }
    });
  }, [sessionId, isArchivedView, lines.length]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleCloudReady = (event) => {
      const state = event?.detail || window.__morpheusSyncState || {};
      // Once this browser has started editing an already-hydrated live count,
      // background cloud-ready events must never restart hydration. Restarting
      // the detail effect can momentarily apply an older Firestore snapshot and
      // blank fields the counter has just entered. The active browser owns the
      // visible form until remount/refresh; persistence continues independently.
      if (hasHydratedRef.current && localSessionUiAuthorityRef.current) return;
      // Any resolved org is enough for critical session-detail reads/writes; the
      // full inventory snapshot does not need to finish hydrating first.
      if (state?.orgId) setCloudAuthorityEpoch((value) => value + 1);
    };
    window.addEventListener("mc-cloud-sync-ready", handleCloudReady);
    // If the org resolved just before this component mounted, schedule one
    // authority refresh without waiting for another sync event.
    if (window.__morpheusSyncState?.orgId) {
      const timer = window.setTimeout(() => setCloudAuthorityEpoch((value) => value + 1), 0);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener("mc-cloud-sync-ready", handleCloudReady);
      };
    }
    return () => window.removeEventListener("mc-cloud-sync-ready", handleCloudReady);
  }, []);


  useEffect(() => {
    setOrderSheets(loadOrderSheets());
  }, [orderModalOpen]);

  useEffect(() => {
    if (quickFilter !== "uncounted" && heldUncountedIds.size) {
      setHeldUncountedIds(new Set());
    }
  }, [quickFilter, heldUncountedIds.size]);

  useEffect(() => {
    let cancelled = false;

    const tryParse = (raw) => {
      try { return raw ? JSON.parse(raw) : null; } catch (err) { console.error(err); return null; }
    };

    const payloadRevision = (payload) => Number(payload?.meta?.liveRevision ?? payload?.liveRevision ?? 0) || 0;
    const payloadTouchedCount = (payload) => (Array.isArray(payload?.lines) ? payload.lines : []).filter((line) =>
      String(line?.counted ?? "").trim() || String(line?.waste ?? "").trim() || String(line?.note ?? "").trim()
    ).length;
    const payloadSavedTime = (payload) => Date.parse(payload?.savedAt || payload?.meta?.lastEditedAt || 0) || 0;
    const payloadIsTerminal = (payload) => {
      const status = String(payload?.meta?.status || "").trim().toLowerCase();
      return Boolean(payload?.meta?.sectionCompletedAt || payload?.meta?.completedAt || ["section complete", "completed", "complete"].includes(status));
    };
    const chooseBestLivePayload = (...payloads) => payloads
      .filter((payload) => payload && Array.isArray(payload?.lines) && payload.lines.length)
      .sort((a, b) => {
        const revisionDelta = payloadRevision(b) - payloadRevision(a);
        if (revisionDelta) return revisionDelta;
        const touchedDelta = payloadTouchedCount(b) - payloadTouchedCount(a);
        if (touchedDelta) return touchedDelta;
        return payloadSavedTime(b) - payloadSavedTime(a);
      })[0] || null;
    // Once Sessions says a count is complete, row completeness is more important
    // than a later autosave revision. This recovers older completed sessions where
    // an in-flight partial autosave landed after the 99/99 completion snapshot.
    const chooseBestCompletedPayload = (...payloads) => composeCompletedAuthority(...payloads);

    const applyDetailPayload = (payload, fallbackArchivedRecord = null, authority = "detail") => {
      // Background re-hydration must not replace an actively edited form, but the
      // live Firestore subscription is different: it arrives already merged with
      // this browser's locally-owned fields and is allowed to advance untouched
      // rows/status from another device. This keeps the UI smooth *and* genuinely
      // live across devices without reintroducing disappearing keystrokes.
      if (!isArchivedView && hasHydratedRef.current && localSessionUiAuthorityRef.current && authority !== "cloud-live") return;
      const parsedLines = Array.isArray(payload?.lines) ? payload.lines : [];
      const parsedMeta = payload?.meta || {};
      const fallbackRecord = fallbackArchivedRecord || (loadSessionRecords() || []).find((record) => record?.sessionId === sessionId) || {};
      const fullSite = parsedMeta.fullSite === true || fallbackRecord?.fullSite === true || String(parsedMeta.countScope || fallbackRecord?.countScope || parsedMeta.sessionType || fallbackRecord?.sessionType || '').toLowerCase().includes('full');
      setMeta({
        ...fallbackRecord,
        ...parsedMeta,
        sessionId,
        createdAt: parsedMeta.createdAt || fallbackRecord?.createdAt || fallbackRecord?.preparedAt || null,
        preparedAt: parsedMeta.preparedAt || fallbackRecord?.preparedAt || fallbackRecord?.createdAt || null,
        startedAt: parsedMeta.startedAt || fallbackRecord?.startedAt || null,
        sectionCompletedAt: parsedMeta.sectionCompletedAt || fallbackRecord?.sectionCompletedAt || null,
        completedAt: parsedMeta.completedAt || fallbackRecord?.completedAt || fallbackRecord?.sectionCompletedAt || null,
        verifiedAt: parsedMeta.verifiedAt || fallbackRecord?.verifiedAt || null,
        verifiedBy: parsedMeta.verifiedBy || fallbackRecord?.verifiedBy || "",
        finalizedAt: parsedMeta.finalizedAt || fallbackRecord?.finalizedAt || null,
        finalizedBy: parsedMeta.finalizedBy || fallbackRecord?.finalizedBy || "",
        archivedAt: parsedMeta.archivedAt || fallbackRecord?.archivedAt || null,
        archivedBy: parsedMeta.archivedBy || fallbackRecord?.archivedBy || fallbackRecord?.lastEditedBy || "",
        restoredAt: parsedMeta.restoredAt || fallbackRecord?.restoredAt || null,
        restoredBy: parsedMeta.restoredBy || fallbackRecord?.restoredBy || "",
        restoreReason: parsedMeta.restoreReason || fallbackRecord?.restoreReason || "",
        restoreAudit: Array.isArray(fallbackRecord?.restoreAudit) && fallbackRecord.restoreAudit.length
          ? fallbackRecord.restoreAudit
          : (Array.isArray(parsedMeta.restoreAudit) ? parsedMeta.restoreAudit : []),
        lifecycleEvents: Array.isArray(fallbackRecord?.lifecycleEvents) && fallbackRecord.lifecycleEvents.length
          ? fallbackRecord.lifecycleEvents
          : (Array.isArray(parsedMeta.lifecycleEvents) ? parsedMeta.lifecycleEvents : []),
        correctionVersions: Array.isArray(parsedMeta.correctionVersions) ? parsedMeta.correctionVersions : (Array.isArray(fallbackRecord?.correctionVersions) ? fallbackRecord.correctionVersions : []),
        status: isArchivedView ? "Archived" : (parsedMeta.status || fallbackRecord?.status || "Active"),
        store: parsedMeta.store || parsedMeta.storeName || fallbackRecord?.store || fallbackRecord?.storeName || fallbackRecord?.siteName || "",
        storeName: parsedMeta.storeName || parsedMeta.store || fallbackRecord?.storeName || fallbackRecord?.store || fallbackRecord?.siteName || "",
        storeKey: parsedMeta.storeKey || fallbackRecord?.storeKey || "",
        sectionId: fullSite ? '' : (parsedMeta.sectionId || fallbackRecord?.sectionId || ''),
        sectionName: fullSite ? '' : (parsedMeta.sectionName || fallbackRecord?.sectionName || ''),
        fullSite,
        sessionType: parsedMeta.sessionType || fallbackRecord?.sessionType || (fullSite ? 'full-count' : 'section'),
        countScope: parsedMeta.countScope || fallbackRecord?.countScope || (fullSite ? 'full-site' : 'section'),
        cycleId: parsedMeta.cycleId || fallbackRecord?.cycleId || '',
        cycleName: parsedMeta.cycleName || fallbackRecord?.cycleName || '',
        startedBy: parsedMeta.startedBy || fallbackRecord?.startedBy || "User",
        lastEditedBy: parsedMeta.lastEditedBy || fallbackRecord?.lastEditedBy || parsedMeta.startedBy || fallbackRecord?.startedBy || "User",
        importedRows: parsedLines.length || parsedMeta.importedRows || fallbackRecord?.totalLines || 0,
      });
      liveRevisionRef.current = Math.max(liveRevisionRef.current, payloadRevision(payload));
      setLines(parsedLines);
      const hydratedSavedAt = payload?.savedAt || fallbackRecord?.updatedAt || fallbackRecord?.archivedAt || null;
      lastSavedAtRef.current = hydratedSavedAt;
      setLastSavedAt(hydratedSavedAt);
      const hydratedTouched = countTouchedRows(parsedLines);
      setSaveProgress({ savedCount: hydratedTouched, targetCount: hydratedTouched, totalCount: parsedLines.length, pending: false });
      detailAuthorityRef.current = authority;
      hasHydratedRef.current = true;
    };

    async function hydrateSessionDetail() {
      const archivedRecord = isArchivedView
        ? loadArchivedSessionRecords().find((item) => item?.sessionId === sessionId)
        : null;

      // P5.3 — finalized Archives consume the write-once final snapshot first.
      // The old session-engine document is compatibility fallback only for legacy
      // archives created before P5.2. A mutable operational/detail document may
      // never override immutable historical evidence.
      if (isArchivedView) {
        const immutableKey = archivedRecord?.finalSnapshotKey || finalSnapshotKeyFor(sessionId);
        const immutablePayload = tryParse(await mcGetCloudItem(immutableKey));
        const immutableLines = Array.isArray(immutablePayload?.lines) ? immutablePayload.lines : [];
        const immutableValid = immutablePayload?.immutable === true && immutablePayload?.snapshotType === "final-count" && immutableLines.length;
        if (immutableValid) {
          if (!cancelled) {
            mcSetCachedItem(immutableKey, JSON.stringify(immutablePayload));
            applyDetailPayload(immutablePayload, archivedRecord, "immutable-final-snapshot");
          }
          return true;
        }

        const localImmutable = tryParse(mcGetItem(immutableKey));
        const localImmutableLines = Array.isArray(localImmutable?.lines) ? localImmutable.lines : [];
        if (localImmutable?.immutable === true && localImmutable?.snapshotType === "final-count" && localImmutableLines.length) {
          if (!cancelled) applyDetailPayload(localImmutable, archivedRecord, "immutable-final-snapshot-cache");
          return true;
        }

        const cloudPayload = tryParse(await mcGetCloudItem(storageKeyFor(sessionId)));
        const cloudLines = Array.isArray(cloudPayload?.lines) ? cloudPayload.lines : [];
        if (cloudPayload && cloudLines.length) {
          if (!cancelled) applyDetailPayload(cloudPayload, archivedRecord, "legacy-archive-cloud");
          return true;
        }

        // Legacy finalized sessions may pre-date the immutable final snapshot and
        // may have row detail under a migration-era ledger/checkpoint/detail key.
        // Reuse Compare's read-only recovery authority here. This does not repair,
        // restore, rewrite or mutate the archive; it only displays surviving rows.
        try {
          const recoveredArchivePayload = await loadSessionDetailSnapshotForCompare(sessionId, archivedRecord || {});
          const recoveredArchiveLines = Array.isArray(recoveredArchivePayload?.lines) ? recoveredArchivePayload.lines : [];
          if (recoveredArchivePayload && recoveredArchiveLines.length) {
            if (!cancelled) applyDetailPayload(recoveredArchivePayload, archivedRecord, "legacy-archive-recovered-readonly");
            return true;
          }
        } catch (_) {}

        // If the old archive genuinely has no surviving row-level snapshot, do not
        // render it as 0/0 Active. Preserve the factual frozen archive summary
        // (e.g. 101/101) and clearly keep row detail empty rather than inventing it.
        if (archivedRecord) {
          const frozenTotal = Number(
            archivedRecord?.finalTotalLines
            ?? archivedRecord?.totalLines
            ?? archivedRecord?.sessionSummary?.totalLines
            ?? archivedRecord?.meta?.sessionSummary?.totalLines
            ?? 0
          );
          const frozenCompleted = Number(
            archivedRecord?.finalCompletedLines
            ?? archivedRecord?.completedLines
            ?? archivedRecord?.sessionSummary?.completedLines
            ?? archivedRecord?.meta?.sessionSummary?.completedLines
            ?? frozenTotal
            ?? 0
          );
          if (!cancelled) {
            setMeta({
              ...archivedRecord,
              sessionId,
              status: "Archived",
              importedRows: frozenTotal,
              historicalTotalLines: frozenTotal,
              historicalCompletedLines: frozenCompleted,
              rowDetailUnavailable: frozenTotal > 0,
            });
            setLines([]);
            setSaveProgress({
              savedCount: frozenCompleted,
              targetCount: frozenCompleted,
              totalCount: frozenTotal,
              pending: false,
            });
            detailAuthorityRef.current = "archive-summary-only";
            hasHydratedRef.current = true;
          }
          return true;
        }
      }

      const localPayload = tryParse(mcGetItem(storageKeyFor(sessionId)));
      const localLines = Array.isArray(localPayload?.lines) ? localPayload.lines : [];

      // Phase 2 foundation: one canonical Count Ledger per session. The previous
      // engine/checkpoint/completed documents remain migration + recovery mirrors,
      // but they are no longer peers. Hydration always prefers the ledger first.
      if (!isArchivedView) {
        const localLedger = tryParse(mcGetItem(countLedgerKeyFor(sessionId)));
        const localCheckpoint = tryParse(mcGetItem(checkpointKeyFor(sessionId)));
        const localCompleted = tryParse(mcGetItem(completedSnapshotKeyFor(sessionId)));
        const [ledgerRaw, cloudRaw, checkpointRaw, completedRaw] = await Promise.all([
          mcGetCloudItem(countLedgerKeyFor(sessionId)),
          mcGetCloudItem(storageKeyFor(sessionId)),
          mcGetCloudItem(checkpointKeyFor(sessionId)),
          mcGetCloudItem(completedSnapshotKeyFor(sessionId)),
        ]);
        const cloudLedger = tryParse(ledgerRaw);
        const cloudPayload = tryParse(cloudRaw);
        const cloudCheckpoint = tryParse(checkpointRaw);
        const cloudCompleted = tryParse(completedRaw);
        const sessionRecord = (loadSessionRecords() || []).find((record) => record?.sessionId === sessionId) || {};
        const recordStatus = String(sessionRecord?.status || "").trim().toLowerCase();
        const recordIsTerminal = Boolean(sessionRecord?.sectionCompletedAt || sessionRecord?.completedAt || ["section complete", "completed", "complete"].includes(recordStatus));
        const ledgerAuthority = recordIsTerminal
          ? chooseBestCompletedPayload(cloudLedger, localLedger)
          : chooseBestLivePayload(cloudLedger, localLedger);
        const recoveryAuthority = recordIsTerminal
          ? chooseBestCompletedPayload(localCompleted, cloudCompleted, localPayload, localCheckpoint, cloudPayload, cloudCheckpoint)
          : chooseBestLivePayload(localPayload, localCheckpoint, cloudPayload, cloudCheckpoint, localCompleted, cloudCompleted);
        let preferredPayload = ledgerAuthority || recoveryAuthority;
        // MC422 — if Sessions says this count is terminal but all exact-key reads
        // are empty, do not paint a fake 0/0 completed count. Ask the shared rescue
        // layer to inspect IndexedDB + every durable Firestore compatibility mirror,
        // rebuild the canonical ledger, and hydrate from the recovered rows.
        if (!preferredPayload && recordIsTerminal) {
          try {
            const repaired = await ensureCanonicalCountLedger(sessionId, sessionRecord);
            if (repaired?.ok && repaired?.payload && Array.isArray(repaired.payload.lines) && repaired.payload.lines.length) {
              preferredPayload = repaired.payload;
            }
          } catch (_) {}
        }
        if (preferredPayload) {
          if (!cancelled && !liveCloudApplied) {
            const authority = preferredPayload === cloudLedger ? "ledger-cloud"
              : preferredPayload === localLedger ? "ledger-local"
              : preferredPayload === cloudPayload || preferredPayload === cloudCheckpoint || preferredPayload === cloudCompleted ? "recovery-cloud" : "recovery-local";
            const outbox = readLiveSaveOutbox(sessionId);
            const recoveredPayload = applyLiveSaveOutbox(preferredPayload, outbox);
            const preferredRaw = JSON.stringify(recoveredPayload);
            // Self-migrate old sessions into the canonical ledger on first healthy open.
            // Also replay a pending per-field outbox that survived an interrupted
            // autosave. Firestore acknowledgement is required before the journal is
            // cleared, so a slow/offline network cannot silently discard the edits.
            if ((!ledgerAuthority && recoveryAuthority) || outbox) {
              mcSetCachedItem(countLedgerKeyFor(sessionId), preferredRaw);
              mcSetCloudItemImmediate(countLedgerKeyFor(sessionId), preferredRaw)
                .then((ok) => { if (ok && outbox) clearLiveSaveOutbox(sessionId); })
                .catch(() => {});
            }
            mcSetCachedItem(storageKeyFor(sessionId), preferredRaw);
            applyDetailPayload(recoveredPayload, archivedRecord, outbox ? `${authority}-outbox-recovered` : authority);
          }
          return true;
        }
      } else if (localPayload && localLines.length > 0) {
        if (!cancelled) applyDetailPayload(localPayload, archivedRecord, "local");
        return true;
      }

      const archiveSnapshot = archivedRecord?.detailSnapshot && typeof archivedRecord.detailSnapshot === "object" ? archivedRecord.detailSnapshot : null;
      const archiveLines = Array.isArray(archiveSnapshot?.lines) ? archiveSnapshot.lines : [];
      if (archiveSnapshot && archiveLines.length) {
        if (!cancelled) applyDetailPayload(archiveSnapshot, archivedRecord, "archive");
        return true;
      }

      return false;
    }

    let liveCloudApplied = false;
    let unsubscribeLiveDetail = () => {};

    // Live-count detail is operational state shared across devices. Local cache gives
    // us a fast first paint, but Firestore must keep an observer/viewer in sync with
    // the device that is actively counting. Without this subscription, refreshing an
    // admin/viewer tab could remain at the prepared 0/N seed while the session list
    // correctly advanced from another device.
    if (!isArchivedView) {
      unsubscribeLiveDetail = mcSubscribeCloudItem(countLedgerKeyFor(sessionId), (rawValue) => {
        if (cancelled || !rawValue) return;
        // Keep this browser authoritative only for fields it actually edited.
        // Untouched fields must continue to receive remote-device updates; blocking
        // the whole cloud snapshot made observer tabs freeze at e.g. 96/99 while
        // another device legitimately reached and completed 99/99.
        const cloudPayload = tryParse(rawValue);
        const cloudLines = Array.isArray(cloudPayload?.lines) ? cloudPayload.lines : [];
        if (!cloudPayload || !cloudLines.length) return;

        const currentMeta = latestMetaRef.current || {};
        const currentStatus = String(currentMeta?.status || "").trim().toLowerCase();
        const currentTerminal = Boolean(currentMeta?.sectionCompletedAt || currentMeta?.completedAt || ["section complete", "completed", "complete"].includes(currentStatus));
        const incomingStatus = String(cloudPayload?.meta?.status || "").trim().toLowerCase();
        const incomingTerminal = Boolean(cloudPayload?.meta?.sectionCompletedAt || cloudPayload?.meta?.completedAt || ["section complete", "completed", "complete"].includes(incomingStatus));
        const currentTouched = (latestLinesRef.current || []).filter((line) => String(line?.counted ?? "").trim() || String(line?.waste ?? "").trim() || String(line?.note ?? "").trim()).length;
        const incomingTouched = payloadTouchedCount(cloudPayload);
        // A completed count is frozen. Never let a late Active/partial autosave
        // downgrade the locked view from 99/99 to 57/99 (or similar).
        if (currentTerminal && (!incomingTerminal || incomingTouched < currentTouched)) return;

        // The terminal envelope can occasionally arrive with fewer rows than the
        // browser/checkpoint already has (for example 76/99 immediately after Complete).
        // Accept the completion lifecycle metadata, but preserve the fuller row values.
        // This is the critical guard that stops "Complete" itself from making counts vanish.
        let authoritativeCloudPayload = cloudPayload;
        if (incomingTerminal && currentTouched > incomingTouched && Array.isArray(latestLinesRef.current) && latestLinesRef.current.length) {
          authoritativeCloudPayload = {
            ...cloudPayload,
            meta: { ...(latestMetaRef.current || {}), ...(cloudPayload.meta || {}) },
            lines: latestLinesRef.current,
          };
        }

        const incomingRevision = payloadRevision(authoritativeCloudPayload);
        const currentRevision = Number(liveRevisionRef.current || 0);
        if (currentRevision && incomingRevision < currentRevision) return;
        if (incomingRevision > currentRevision) liveRevisionRef.current = incomingRevision;

        const incomingSavedAt = Date.parse(authoritativeCloudPayload?.savedAt || authoritativeCloudPayload?.meta?.lastEditedAt || 0) || 0;
        const currentSavedAt = Date.parse(lastSavedAtRef.current || 0) || 0;

        // Do not globally block cloud updates just because this browser is typing.
        // Per-field optimistic authority below protects every locally edited input.
        // We only reject snapshots that are clearly older than the last accepted
        // durable snapshot; newer remote rows/status are allowed through.
        if (currentSavedAt && incomingSavedAt && incomingSavedAt < currentSavedAt) return;

        // Merge the cloud snapshot with any locally-dirty fields. Firestore may
        // legitimately echo a previous snapshot while this browser is counting.
        // Preserve each locally edited value until the cloud payload contains the
        // exact same value; only then release local authority for that field.
        const dirtyFields = localDirtyFieldsRef.current;
        let mergedPayload = authoritativeCloudPayload;
        if (dirtyFields.size) {
          const currentById = new Map((latestLinesRef.current || []).map((line) => [String(line?.id), line]));
          const authoritativeCloudLines = Array.isArray(authoritativeCloudPayload?.lines) ? authoritativeCloudPayload.lines : cloudLines;
          const mergedLines = authoritativeCloudLines.map((cloudLine) => {
            const id = String(cloudLine?.id ?? "");
            const localLine = currentById.get(id);
            if (!localLine) return cloudLine;
            let nextLine = cloudLine;
            for (const key of ["counted", "waste", "note"]) {
              const dirtyKey = `${id}:${key}`;
              if (!dirtyFields.has(dirtyKey)) continue;
              // The dirty-field map stores the exact value entered by this browser.
              // Do NOT read the optimistic value back from React state here: a
              // Firestore echo can arrive in the same tick as onChange, before the
              // setLines() render/effect has updated latestLinesRef. Reading the
              // ref in that window gives us the previous value and is exactly what
              // made a freshly typed count disappear.
              const optimisticValue = String(dirtyFields.get(dirtyKey) ?? "");
              const cloudValue = String(cloudLine?.[key] ?? "");
              if (cloudValue === optimisticValue) {
                dirtyFields.delete(dirtyKey);
                continue;
              }
              if (nextLine === cloudLine) nextLine = { ...cloudLine };
              nextLine[key] = optimisticValue;
            }
            return nextLine;
          });
          mergedPayload = { ...authoritativeCloudPayload, lines: mergedLines };
        }

        liveCloudApplied = true;
        mcSetCachedItem(storageKeyFor(sessionId), JSON.stringify(mergedPayload));
        applyDetailPayload(mergedPayload, null, "cloud-live");
      });
    }

    let fallbackRetryTimer = null;
    let fallbackRetryAttempt = 0;

    const retryAuthoritativeHydration = async () => {
      if (cancelled) return;
      fallbackRetryAttempt += 1;
      const found = await hydrateSessionDetail();
      if (cancelled || found) return;
      // Access Layer / org hydration can briefly make the detail read unavailable.
      // Keep retrying the real local/cloud detail before even considering a seed.
      if (fallbackRetryAttempt < 12) {
        fallbackRetryTimer = window.setTimeout(retryAuthoritativeHydration, 500);
        return;
      }

      const sessionRecord = (loadSessionRecords() || []).find((record) => record?.sessionId === sessionId) || {};
      const fallbackStatus = String(sessionRecord?.status || "").trim().toLowerCase();
      const fallbackTerminal = Boolean(
        sessionRecord?.sectionCompletedAt || sessionRecord?.completedAt ||
        ["section complete", "section-complete", "completed", "complete"].includes(fallbackStatus)
      );
      // MC421 — never render a terminal count as a prepared 0/0 shell. If the
      // normal hydration mirrors were missed, perform one last durable rescue
      // (including migration-era legacy keys) and self-heal the canonical ledger.
      if (fallbackTerminal) {
        try {
          const repaired = await ensureCanonicalCountLedger(sessionId, sessionRecord);
          if (repaired?.ok && repaired?.payload && Array.isArray(repaired.payload.lines) && repaired.payload.lines.length) {
            if (!cancelled) applyDetailPayload(repaired.payload, null, repaired.repaired ? "terminal-ledger-repaired" : "terminal-ledger-recovered");
            return;
          }
        } catch (_) {}
      }
      const scopedSeed = loadPreparedRowsFromSessionSeed(sessionRecord?.storeKey || activeStoreScopeRef.current);
      const seedById = findSessionSeedById(sessionId);
      const seed = (scopedSeed?.sessionId === sessionId ? scopedSeed : null) || seedById;
      const shouldUseSeed = Boolean(seed?.preparedRows?.length && seed?.sessionId === sessionId);
      const sourceRows = shouldUseSeed ? seed.preparedRows : [];
      const fullSite = shouldUseSeed
        ? seed?.fullSite === true || sessionRecord?.fullSite === true || String(seed?.countScope || seed?.sessionType || sessionRecord?.countScope || sessionRecord?.sessionType || '').toLowerCase().includes('full')
        : sessionRecord?.fullSite === true || String(sessionRecord?.countScope || sessionRecord?.sessionType || '').toLowerCase().includes('full');

      setMeta({
        sessionId,
        store: shouldUseSeed ? seed?.storeName || seed?.store || sessionRecord?.storeName || sessionRecord?.store || sessionRecord?.siteName || "" : sessionRecord?.storeName || sessionRecord?.store || sessionRecord?.siteName || "",
        storeName: shouldUseSeed ? seed?.storeName || seed?.store || sessionRecord?.storeName || sessionRecord?.store || sessionRecord?.siteName || "" : sessionRecord?.storeName || sessionRecord?.store || sessionRecord?.siteName || "",
        storeKey: shouldUseSeed ? seed?.storeKey || seed?.mapping?.systemSoh || sessionRecord?.storeKey || "" : sessionRecord?.storeKey || "",
        sectionId: fullSite ? '' : (shouldUseSeed ? seed?.sectionId || sessionRecord?.sectionId || '' : sessionRecord?.sectionId || ''),
        sectionName: fullSite ? '' : (shouldUseSeed ? seed?.sectionName || sessionRecord?.sectionName || '' : sessionRecord?.sectionName || ''),
        fullSite,
        sessionType: shouldUseSeed ? seed?.sessionType || sessionRecord?.sessionType || (fullSite ? 'full-count' : 'section') : sessionRecord?.sessionType || (fullSite ? 'full-count' : 'section'),
        countScope: shouldUseSeed ? seed?.countScope || sessionRecord?.countScope || (fullSite ? 'full-site' : 'section') : sessionRecord?.countScope || (fullSite ? 'full-site' : 'section'),
        cycleId: shouldUseSeed ? seed?.cycleId || sessionRecord?.cycleId || '' : sessionRecord?.cycleId || '',
        cycleName: shouldUseSeed ? seed?.cycleName || sessionRecord?.cycleName || '' : sessionRecord?.cycleName || '',
        status: sessionRecord?.status || "Active",
        startedBy: shouldUseSeed
          ? (seed?.startedBy && seed.startedBy !== "Nelisiwe" ? seed.startedBy : sessionRecord?.startedBy || buildProfileDisplayName(currentProfileRef.current) || "User")
          : sessionRecord?.startedBy || buildProfileDisplayName(currentProfileRef.current) || "User",
        lastEditedBy: sessionRecord?.lastEditedBy || (shouldUseSeed ? seed?.lastEditedBy : '') || sessionRecord?.startedBy || buildProfileDisplayName(currentProfileRef.current) || "User",
        importedRows: sourceRows.length || Number(sessionRecord?.totalLines || 0),
        sourceFile: shouldUseSeed ? seed?.fileName || sessionRecord?.fileName || "prepared import" : sessionRecord?.fileName || "prepared import",
        startedAt: shouldUseSeed ? seed?.startedAt || sessionRecord?.startedAt || new Date().toISOString() : sessionRecord?.startedAt || new Date().toISOString(),
      });

      setLines(
        sourceRows.map((item, index) => ({
          id: item.id || `seed-${index + 1}`,
          product: item.product || '',
          category: item.category === '__unmapped__' ? 'Other' : (item.category || 'Other'),
          sectionId: fullSite ? '' : (shouldUseSeed ? seed?.sectionId || sessionRecord?.sectionId || '' : sessionRecord?.sectionId || ''),
          sectionName: fullSite ? '' : (shouldUseSeed ? seed?.sectionName || sessionRecord?.sectionName || '' : sessionRecord?.sectionName || ''),
          fullSite,
          sessionType: shouldUseSeed ? seed?.sessionType || (fullSite ? 'full-count' : 'section') : sessionRecord?.sessionType || (fullSite ? 'full-count' : 'section'),
          countScope: shouldUseSeed ? seed?.countScope || (fullSite ? 'full-site' : 'section') : sessionRecord?.countScope || (fullSite ? 'full-site' : 'section'),
          cycleId: shouldUseSeed ? seed?.cycleId || sessionRecord?.cycleId || '' : sessionRecord?.cycleId || '',
          cycleName: shouldUseSeed ? seed?.cycleName || sessionRecord?.cycleName || '' : sessionRecord?.cycleName || '',
          sku: item.sku || "—",
          cost: Number(item.cost || 0),
          systemSoh: roundTo2(item.realSoh ?? item.systemSoh ?? 0),
          counted: "",
          waste: "",
          note: "",
        }))
      );
      detailAuthorityRef.current = "seed-fallback";
      hasHydratedRef.current = true;
    };

    hydrateSessionDetail().then((foundDetail) => {
      if (cancelled || foundDetail) return;
      fallbackRetryTimer = window.setTimeout(retryAuthoritativeHydration, 500);
    }).catch((err) => {
      console.warn('Failed to hydrate live count, using safe session shell', err);
      if (cancelled) return;
      const sessionRecord = (loadSessionRecords() || []).find((record) => record?.sessionId === sessionId) || {};
      const fullSite = sessionRecord?.fullSite === true || String(sessionRecord?.countScope || sessionRecord?.sessionType || '').toLowerCase().includes('full');
      setMeta({
        sessionId,
        store: sessionRecord?.storeName || sessionRecord?.store || sessionRecord?.siteName || '',
        storeName: sessionRecord?.storeName || sessionRecord?.store || sessionRecord?.siteName || '',
        storeKey: sessionRecord?.storeKey || '',
        sectionId: fullSite ? '' : (sessionRecord?.sectionId || ''),
        sectionName: fullSite ? '' : (sessionRecord?.sectionName || ''),
        fullSite,
        sessionType: sessionRecord?.sessionType || (fullSite ? 'full-count' : 'section'),
        countScope: sessionRecord?.countScope || (fullSite ? 'full-site' : 'section'),
        cycleId: sessionRecord?.cycleId || '',
        cycleName: sessionRecord?.cycleName || '',
        status: sessionRecord?.status || 'Active',
        startedBy: sessionRecord?.startedBy || buildProfileDisplayName(currentProfileRef.current) || 'User',
        importedRows: Number(sessionRecord?.totalLines || 0),
        sourceFile: sessionRecord?.fileName || 'prepared import',
        startedAt: sessionRecord?.startedAt || new Date().toISOString(),
      });
      setLines([]);
      detailAuthorityRef.current = "shell";
      hasHydratedRef.current = true;
    });

    return () => {
      cancelled = true;
      if (fallbackRetryTimer) window.clearTimeout(fallbackRetryTimer);
      try { unsubscribeLiveDetail?.(); } catch (_) {}
    };
  }, [sessionId, isArchivedView, cloudAuthorityEpoch]);

  useEffect(() => {
    if (!meta || !lines.length || !hasHydratedRef.current || !hasUserEditedRef.current) return undefined;
    if (["seed-fallback", "shell", "loading"].includes(detailAuthorityRef.current)) return undefined;

    setSaveState("saving");
    if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current);

    // Correction drafts stay local-only and retain the short idle save because
    // they do not create the expensive live Count Ledger Firestore traffic.
    if (correctionMode) {
      saveTimeoutRef.current = window.setTimeout(() => {
        const liveSessionSummary = buildLiveSessionSummary(lines, meta, activeStoreScope, flagConfig, unitEngineConfig);
        const payload = { meta: { ...meta, liveRevision: liveRevisionRef.current, liveSessionSummary }, lines, savedAt: new Date().toISOString() };
        mcSetItem(correctionDraftKeyFor(sessionId), JSON.stringify({
          ...payload,
          correctionReason,
          correctionOtherReason,
          correctionStartedAt,
          originalLines: originalCorrectionLines,
        }));
        setSaveState("saved");
        hasUserEditedRef.current = false;
      }, 420);

      return () => {
        if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current);
      };
    }

    // MC489 — local-first cloud checkpointing. The recovery outbox has already
    // captured each individual field edit synchronously. A whole-ledger Firestore
    // checkpoint is therefore needed only after a small batch or the maximum
    // window, rather than after nearly every human pause between products.
    const nowMs = Date.now();
    if (!checkpointWindowStartedAtRef.current) checkpointWindowStartedAtRef.current = nowMs;
    const elapsedMs = Math.max(0, nowMs - checkpointWindowStartedAtRef.current);
    const batchReady = editsSinceCloudCheckpointRef.current >= LIVE_LEDGER_CHECKPOINT_EDIT_BATCH;
    const delayMs = batchReady
      ? LIVE_LEDGER_CHECKPOINT_MIN_DELAY_MS
      : Math.max(LIVE_LEDGER_CHECKPOINT_MIN_DELAY_MS, LIVE_LEDGER_CHECKPOINT_MAX_MS - elapsedMs);

    saveTimeoutRef.current = window.setTimeout(() => {
      if (!hasUserEditedRef.current || !latestMetaRef.current || !latestLinesRef.current?.length) return;
      const snapshotMeta = latestMetaRef.current;
      const snapshotLines = latestLinesRef.current;
      const now = new Date().toISOString();
      const liveSessionSummary = buildLiveSessionSummary(snapshotLines, snapshotMeta, activeStoreScope, flagConfig, unitEngineConfig);
      const payload = {
        meta: { ...snapshotMeta, liveRevision: liveRevisionRef.current, liveSessionSummary },
        lines: snapshotLines,
        savedAt: now,
      };
      const raw = JSON.stringify(payload);
      const localSavedAtMs = Date.parse(now) || Date.now();

      // Keep the exact same local recovery mirrors and canonical Count Ledger
      // queue used by the proven baseline. Only the cloud checkpoint cadence has
      // changed; merge/version/recovery semantics remain untouched.
      mcSetCachedItem(countLedgerKeyFor(sessionId), raw);
      mcSetCachedItem(storageKeyFor(sessionId), raw);
      mcSetCachedItem(checkpointKeyFor(sessionId), raw);
      latestLocalSnapshotAtRef.current = Math.max(latestLocalSnapshotAtRef.current, localSavedAtMs);
      lastSavedAtRef.current = now;
      setLastSavedAt(now);
      setSaveState("saving");

      lastQueuedEditSequenceRef.current = localEditSequenceRef.current;
      editsSinceCloudCheckpointRef.current = 0;
      checkpointWindowStartedAtRef.current = 0;
      enqueueLiveCountSave({
        sessionId,
        cloudKey: countLedgerKeyFor(sessionId),
        raw,
        touchedCount: countTouchedRows(snapshotLines),
        totalCount: snapshotLines.length,
        orgId: getMcActiveOrgId(),
      });
      setSaveProgress((current) => ({
        ...current,
        targetCount: countTouchedRows(snapshotLines),
        totalCount: snapshotLines.length,
        pending: true,
      }));
    }, delayMs);

    return () => {
      if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current);
    };
  }, [meta, lines, sessionId, correctionMode, correctionReason, correctionOtherReason, correctionStartedAt, originalCorrectionLines, activeStoreScope, flagConfig, unitEngineConfig]);

  const isAllowedStore = useMemo(() => {
    if (!meta?.store) return true;
    return filterRecordsForProfile([{ storeName: meta.store, store: meta.store }], currentProfile).length > 0;
  }, [adminStoreView, currentProfile, meta]);

  const mosPdfConfig = useMemo(() => {
    const scope = meta?.store || activeStoreScope || "ALL";
    const stored = loadEngineState(scope) || loadEngineState("ALL") || {};
    const storedConfig = stored?.config || {};
    return {
      // Flag config is stored separately from engine config. Always use resolveFlagConfig
      // so new sites inherit the ALL SITES MOS settings unless they have their own override.
      hideCountedInMosPdf: Boolean(flagConfig?.hideCountedInMosPdf ?? false),
      hideZeroOrderItemsInMosPdf: Boolean(flagConfig?.hideZeroOrderItemsInMosPdf ?? false),
      categoryGroups: Array.isArray(storedConfig.categoryGroups) ? storedConfig.categoryGroups : [],
      categoryOrder: Array.isArray(storedConfig.categoryOrder) ? storedConfig.categoryOrder : [],
      engineState: stored,
    };
  }, [meta?.store, activeStoreScope, flagConfig?.hideCountedInMosPdf, flagConfig?.hideZeroOrderItemsInMosPdf]);

  const countGroupConfig = useMemo(() => {
    const scope = meta?.store || activeStoreScope || "ALL";
    const stored = loadEngineState(scope) || loadEngineState("ALL");
    const categoryOrder = Array.isArray(stored?.config?.categoryOrder) ? stored.config.categoryOrder : [];
    const categoryRank = buildCategoryRank(categoryOrder);
    const groups = Array.isArray(stored?.config?.categoryGroups)
      ? [...stored.config.categoryGroups].sort((a, b) => {
          const ar = groupRank(a, categoryRank);
          const br = groupRank(b, categoryRank);
          if (ar !== br) return ar - br;
          return String(a?.name || "").localeCompare(String(b?.name || ""));
        })
      : [];
    return { groups, categoryOrder, categoryRank };
  }, [meta?.store, activeStoreScope]);

  const countCategoryGroups = countGroupConfig.groups;

  const totals = useMemo(() => {
    // Historical summary-only archives must remain truthful. A legacy archive can
    // factually record 101/101 even when its old row snapshot no longer survives.
    // In that case show the frozen summary in the KPI cards, but keep the row table
    // empty and never manufacture product values.
    if (isArchivedView && !lines.length && meta?.rowDetailUnavailable) {
      const totalLines = Number(
        meta?.historicalTotalLines
        ?? meta?.finalTotalLines
        ?? meta?.totalLines
        ?? meta?.importedRows
        ?? 0
      );
      const completedLines = Number(
        meta?.historicalCompletedLines
        ?? meta?.finalCompletedLines
        ?? meta?.completedLines
        ?? totalLines
        ?? 0
      );
      const flaggedCount = Number(meta?.finalFlagCount ?? meta?.flaggedCount ?? 0);
      const totalExposure = Number(meta?.finalExposure ?? meta?.costExposure ?? 0);
      return {
        rows: [],
        flaggedCount,
        totalExposure: roundTo2(totalExposure),
        completedLines,
        totalLines,
        remainingLines: Math.max(totalLines - completedLines, 0),
      };
    }

    let totalExposure = 0;
    let flaggedCount = 0;
    let completedLines = 0;

    const rows = lines.map((line) => {
      const touched = line.counted !== "" || line.waste !== "" || line.note?.trim();
      const countedValue = touched ? liveCountNumber(line.counted) : null;
      const wasteValue = touched ? liveCountNumber(line.waste) : 0;
      const unitCost = resolveLineUnitCost(line, meta, activeStoreScope, unitEngineConfig);
      const variance = touched ? roundTo2(Number(countedValue || 0) + Number(wasteValue || 0) - Number(line.systemSoh || 0)) : null;
      const costVariance = touched ? roundTo2(Number(variance || 0) * Number(unitCost.cost || 0)) : null;
      const noteFlagged = Boolean(line.note?.trim());
      const thresholdFlags = touched ? resolveCostVarianceFlagState(costVariance, flagConfig) : { negativeFlagged: false, positiveFlagged: false };
      const { negativeFlagged, positiveFlagged } = thresholdFlags;
      const flagged = noteFlagged || negativeFlagged || positiveFlagged;
      const flagDirection = positiveFlagged ? "positive" : negativeFlagged ? "negative" : noteFlagged ? "note" : null;

      if (touched) completedLines += 1;
      if (flagged) flaggedCount += 1;
      if (touched) totalExposure += Number(costVariance || 0);

      return {
        ...line,
        ...unitCost,
        variance,
        costVariance,
        flagged,
        flagDirection,
        touched,
      };
    });

    // MC468 — once a section is completed, the canonical sessionSummary frozen at
    // completion is the authority for the locked KPI metrics. The row render can
    // briefly hydrate from an older equal-progress snapshot after navigating back
    // into a completed count; recalculating the headline exposure from those rows
    // made Detail disagree with Sessions (for example -R376 vs the frozen -R441).
    // Keep the rows untouched for review, but never replace terminal summary truth
    // with a fresh display-time recalculation. Correction Mode deliberately keeps
    // using the live recalculation below so audited changes remain responsive.
    const terminalMeta = Boolean(
      meta?.sectionCompletedAt ||
      meta?.completedAt ||
      String(meta?.status || "").trim().toLowerCase() === "section complete" ||
      String(meta?.status || "").trim().toLowerCase() === "completed"
    );
    const frozenSummary = terminalMeta && !correctionMode && meta?.sessionSummary && typeof meta.sessionSummary === "object"
      ? meta.sessionSummary
      : null;

    // MC497 — zero threshold boundaries mean "flag any NON-ZERO variance". Older
    // completed summaries could have frozen every counted zero-variance row as a
    // flag because 0 satisfies both <= 0 and >= 0. While that boundary is active,
    // re-derive the flag count from the immutable rows with the corrected sign rule.
    const resolvedFlaggedCount = frozenSummary && !hasZeroCostVarianceFlagBoundary(flagConfig)
      ? Number(frozenSummary.flaggedCount ?? frozenSummary.flaggedItems ?? frozenSummary.flaggedProducts ?? flaggedCount)
      : flaggedCount;
    const resolvedExposure = frozenSummary
      ? Number(frozenSummary.exposure ?? frozenSummary.costExposure ?? frozenSummary.finalExposure ?? totalExposure)
      : totalExposure;
    const resolvedCompletedLines = frozenSummary
      ? Number(frozenSummary.completedLines ?? completedLines)
      : completedLines;
    const resolvedTotalLines = frozenSummary
      ? Number(frozenSummary.totalLines ?? lines.length)
      : lines.length;

    return {
      rows,
      flaggedCount: resolvedFlaggedCount,
      totalExposure: roundTo2(resolvedExposure),
      completedLines: resolvedCompletedLines,
      totalLines: resolvedTotalLines,
      remainingLines: Math.max(resolvedTotalLines - resolvedCompletedLines, 0),
    };
  }, [lines, meta, activeStoreScope, flagConfig, isArchivedView, correctionMode, unitEngineConfig]);

  useEffect(() => {
    if (!meta || isArchivedView || correctionMode) return;
    // Never publish 0/N metrics from the prepared seed while authoritative live
    // detail is temporarily unavailable. That was the root cause of Sessions
    // reverting a real in-progress count to 0 after Access Layer/refresh.
    if (["seed-fallback", "shell", "loading"].includes(detailAuthorityRef.current)) return;
    const terminalMeta = Boolean(meta.sectionCompletedAt || meta.completedAt || meta.status === "Section Complete" || meta.status === "Completed");
    // MC485 — mc-session-list is discovery/routing metadata, not the live metrics
    // channel. Active Sessions already consumes progress/flags/exposure from the
    // canonical Count Ledger observer (MC483). Rebuilding + cloud-writing the whole
    // session index after every keystroke caused avoidable main-thread work and
    // Firestore churn. Keep this compatibility upsert only for terminal hydration;
    // explicit Complete/Correction lifecycle handlers still publish immediately.
    if (!terminalMeta) return;
    const resolvedSection = resolveSectionFromCountLines(lines, meta);
    const frozenSummary = meta?.sessionSummary || null;
    upsertSessionRecord({
      sessionId: meta.sessionId,
      storeName: meta.store,
      fileName: meta.sourceFile,
      startedAt: meta.startedAt,
      updatedAt: meta.lastEditedAt || lastSavedAt || meta.startedAt,
      startedBy: meta.startedBy,
      lastEditedBy: meta.lastEditedBy || meta.startedBy,
      sectionId: resolvedSection.sectionId || meta.sectionId || '',
      sectionName: resolvedSection.sectionName || meta.sectionName || '',
      totalLines: Number(frozenSummary?.totalLines ?? totals.totalLines),
      completedLines: Number(frozenSummary?.completedLines ?? totals.completedLines),
      flaggedCount: Number(frozenSummary?.flaggedCount ?? frozenSummary?.flaggedItems ?? totals.flaggedCount),
      status: terminalMeta ? "Section Complete" : "Active",
      sectionCompletedAt: meta.sectionCompletedAt || null,
      costExposure: Number(frozenSummary?.costExposure ?? frozenSummary?.exposure ?? totals.totalExposure),
      totalExposure: Number(frozenSummary?.exposure ?? frozenSummary?.costExposure ?? totals.totalExposure),
      exposure: Number(frozenSummary?.exposure ?? frozenSummary?.costExposure ?? totals.totalExposure),
      finalExposure: Number(frozenSummary?.finalExposure ?? frozenSummary?.exposure ?? totals.totalExposure),
      sessionSummary: frozenSummary || undefined,
    });
  }, [meta, totals, lastSavedAt, isArchivedView, correctionMode]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return totals.rows.filter((line) => {
      const matchesSearch =
        !term ||
        String(line.product || "").toLowerCase().includes(term) ||
        String(line.category || "").toLowerCase().includes(term) ||
        String(line.sku || "").toLowerCase().includes(term);

      if (!matchesSearch) return false;
      if (quickFilter === "uncounted") return !line.touched || heldUncountedIds.has(line.id);
      if (quickFilter === "counted") return line.touched;
      if (quickFilter === "flagged") return line.flagged;
      if (quickFilter === "variance") return line.touched && Number(line.variance || 0) !== 0;
      return true;
    });
  }, [totals.rows, search, quickFilter, heldUncountedIds]);

  const groupedFilteredRows = useMemo(() => {
    const orderedGroupNames = (countCategoryGroups || []).map((group) => group?.name).filter(Boolean);
    const map = new Map();

    filteredRows.forEach((line) => {
      const groupName = resolveCountGroupName(line.category, countCategoryGroups);
      if (!map.has(groupName)) map.set(groupName, []);
      map.get(groupName).push(line);
    });

    const ordered = [];
    orderedGroupNames.forEach((groupName) => {
      if (map.has(groupName)) {
        ordered.push({ name: groupName, rows: sortRowsByCategoryOrder(map.get(groupName), countGroupConfig.categoryRank) });
        map.delete(groupName);
      }
    });

    Array.from(map.keys()).sort((a, b) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b))).forEach((groupName) => {
      ordered.push({ name: groupName, rows: sortRowsByCategoryOrder(map.get(groupName), countGroupConfig.categoryRank) });
    });

    return ordered;
  }, [filteredRows, countCategoryGroups, countGroupConfig.categoryRank]);

  const toggleGroup = (groupName) => {
    setCollapsedGroups((current) => ({ ...current, [groupName]: !(current[groupName] ?? true) }));
  };

  const normalizeInputValue = (key, value) => {
    if (key !== "counted" && key !== "waste") return value;
    const raw = String(value ?? "");
    if (/^0{2,}$/.test(raw.trim())) return "0";
    return value;
  };

  const updateLine = (id, key, value) => {
    if (isArchivedView || (sectionCompleted && !correctionMode)) return;
    // Do not allow a blank recovery seed to become a destructive live write.
    // Wait for the authoritative detail to hydrate first.
    if (["seed-fallback", "shell", "loading"].includes(detailAuthorityRef.current)) return;
    const cleanValue = normalizeInputValue(key, value);
    if (trafficProbeEnabled) recordMcTrafficEdit(sessionId);
    ledgerFieldSequenceRef.current += 1;
    const fieldVersion = {
      at: Date.now(),
      writer: ledgerWriterIdRef.current,
      seq: ledgerFieldSequenceRef.current,
    };
    if (quickFilter === "uncounted" && (key === "counted" || key === "waste" || key === "note")) {
      setHeldUncountedIds((current) => {
        const next = new Set(current);
        next.add(id);
        return next;
      });
    }
    hasUserEditedRef.current = true;
    localSessionUiAuthorityRef.current = true;
    localEditSequenceRef.current += 1;
    editsSinceCloudCheckpointRef.current += 1;
    if (!checkpointWindowStartedAtRef.current) checkpointWindowStartedAtRef.current = Date.now();
    liveRevisionRef.current += 1;
    latestLocalSnapshotAtRef.current = Date.now();
    localDirtyFieldsRef.current.set(`${String(id)}:${key}`, String(cleanValue ?? ""));
    // While the counter is actively typing, never repaint the controlled input
    // from a remote echo. Other browsers still receive every Firestore update;
    // this guard only protects the browser producing the keystrokes.
    localTypingAuthorityUntilRef.current = Date.now() + 1800;
    const editTimestamp = new Date().toISOString();
    setMeta((current) => {
      if (!current) return current;
      const nextMeta = {
        ...current,
        lastEditedBy: buildProfileDisplayName(currentProfileRef.current) || current.lastEditedBy || current.startedBy || "User",
        lastEditedAt: editTimestamp,
        liveRevision: liveRevisionRef.current,
      };
      latestMetaRef.current = nextMeta;
      return nextMeta;
    });
    setLines((current) => {
      const next = current.map((line) => (line.id === id ? {
        ...line,
        [key]: cleanValue,
        _mcFieldVersions: {
          ...(line?._mcFieldVersions || {}),
          [key]: fieldVersion,
        },
      } : line));
      // Keep the imperative live-sync ref in lockstep with the keystroke instead
      // of waiting for the post-render effect. This closes the same-tick window
      // where a remote snapshot could otherwise observe stale local state.
      latestLinesRef.current = next;
      // MC484 — keep the keystroke path UI-only. Whole-ledger serialization,
      // local snapshot persistence, live summary calculation and Firestore queueing
      // are intentionally deferred to the single autosave effect below. The tiny
      // per-field recovery journal still runs after this state update, so a sudden
      // refresh/route change cannot lose the operator's latest edit.
      return next;
    });
    // Persist the tiny field delta synchronously before React gets a chance to
    // unmount this route. If the operator leaves while Firestore still says
    // Auto-saving (especially on slow Wi-Fi), the next mount can replay exactly
    // the missing keystrokes rather than falling back to the last acknowledged
    // whole-ledger snapshot.
    writeLiveSaveOutboxField(sessionId, id, key, cleanValue, fieldVersion, liveRevisionRef.current);
    setSaveProgress((current) => ({
      ...current,
      targetCount: countTouchedRows(latestLinesRef.current),
      totalCount: latestLinesRef.current.length,
      pending: true,
    }));
  };


  const beginCorrectionMode = () => {
    if (!correctionReason || (correctionReason === "Other" && !correctionOtherReason.trim())) return;
    const startedAt = new Date().toISOString();
    setOriginalCorrectionLines(lines.map((line) => ({ ...line })));
    setCorrectionStartedAt(startedAt);
    setCorrectionMode(true);
    setCorrectionInfoOpen(false);
    setSaveState("saved");
  };

  const cancelCorrectionMode = () => {
    if (originalCorrectionLines.length) setLines(originalCorrectionLines.map((line) => ({ ...line })));
    setCorrectionMode(false);
    setCorrectionStartedAt(null);
    setCorrectionReason("");
    setCorrectionOtherReason("");
    setOriginalCorrectionLines([]);
    mcSetItem(correctionDraftKeyFor(sessionId), "");
    hasUserEditedRef.current = false;
    setSaveState("saved");
  };

  const originalLineById = useMemo(() => new Map(originalCorrectionLines.map((line) => [line.id, line])), [originalCorrectionLines]);
  const correctionChanges = useMemo(() => lines.filter((line) => {
    const original = originalLineById.get(line.id);
    if (!original) return false;
    return String(line.counted ?? "") !== String(original.counted ?? "")
      || String(line.waste ?? "") !== String(original.waste ?? "")
      || String(line.note ?? "") !== String(original.note ?? "");
  }), [lines, originalLineById]);

  const correctionOriginalSummary = useMemo(() => {
    let exposure = 0;
    let flagged = 0;
    originalCorrectionLines.forEach((line) => {
      const touched = line.counted !== "" || line.waste !== "" || line.note?.trim();
      if (!touched) return;
      const countedValue = liveCountNumber(line.counted);
      const wasteValue = liveCountNumber(line.waste);
      const unitCost = resolveLineUnitCost(line, meta, activeStoreScope, unitEngineConfig);
      const variance = roundTo2(Number(countedValue || 0) + Number(wasteValue || 0) - Number(line.systemSoh || 0));
      const costVariance = roundTo2(Number(variance || 0) * Number(unitCost.cost || 0));
      const noteFlagged = Boolean(line.note?.trim());
      const { negativeFlagged, positiveFlagged } = resolveCostVarianceFlagState(costVariance, flagConfig);
      if (noteFlagged || negativeFlagged || positiveFlagged) flagged += 1;
      exposure += Number(costVariance || 0);
    });
    return { exposure: roundTo2(exposure), flagged };
  }, [originalCorrectionLines, meta, activeStoreScope, flagConfig, unitEngineConfig]);

  const correctionRecovery = roundTo2(Number(totals.totalExposure || 0) - Number(correctionOriginalSummary.exposure || 0));
  const correctionActorName = buildProfileDisplayName(currentProfile) || meta?.lastEditedBy || meta?.startedBy || "User";
  const correctionActorRole = String(currentProfile?.roleName || currentProfile?.role || currentProfile?.profileName || currentProfile?.name || "Profile user");

  const submitCorrections = async () => {
    if (!correctionChanges.length || correctionSubmitting) return;
    setCorrectionSubmitting(true);
    try {
      const submittedAt = new Date().toISOString();
      const reason = correctionReason === "Other" ? correctionOtherReason.trim() : correctionReason;
      const changedProducts = correctionChanges.map((line) => {
        const original = originalLineById.get(line.id) || {};
        const currentRow = totals.rows.find((row) => row.id === line.id) || line;
        const originalUnit = resolveLineUnitCost(original, meta, activeStoreScope, unitEngineConfig);
        const originalVariance = roundTo2(liveCountNumber(original.counted) + liveCountNumber(original.waste) - Number(original.systemSoh || 0));
        const originalValue = roundTo2(originalVariance * Number(originalUnit.cost || 0));
        return {
          id: line.id,
          product: line.product,
          sku: line.sku || "",
          category: line.category || "",
          originalCounted: original.counted,
          correctedCounted: line.counted,
          originalWaste: original.waste,
          correctedWaste: line.waste,
          originalNote: original.note || "",
          correctedNote: line.note || "",
          originalExposure: originalValue,
          correctedExposure: roundTo2(currentRow.costVariance || 0),
          valueImpact: roundTo2(Number(currentRow.costVariance || 0) - originalValue),
        };
      });
      const revision = {
        id: `revision:${sessionId}:${Date.now()}`,
        version: Number(meta?.correctionVersions?.length || 0) + 2,
        type: "correction",
        startedAt: correctionStartedAt,
        submittedAt,
        correctedBy: correctionActorName,
        correctedByRole: correctionActorRole,
        reason,
        originalExposure: correctionOriginalSummary.exposure,
        correctedExposure: totals.totalExposure,
        recoveredValue: correctionRecovery,
        changedProducts,
      };
      const correctedSummary = {
        exposure: Number(totals.totalExposure || 0),
        costExposure: Number(totals.totalExposure || 0),
        finalExposure: Number(totals.totalExposure || 0),
        flaggedItems: Number(totals.flaggedCount || 0),
        flaggedCount: Number(totals.flaggedCount || 0),
        flaggedProducts: Number(totals.flaggedCount || 0),
        completedLines: Number(totals.completedLines || 0),
        totalLines: Number(totals.totalLines || 0),
        generatedAt: submittedAt,
        correctedAt: submittedAt,
      };
      const nextMeta = {
        ...meta,
        status: "Section Complete",
        correctedAt: submittedAt,
        correctedBy: correctionActorName,
        correctedByRole: correctionActorRole,
        lastEditedBy: correctionActorName,
        lastEditedAt: submittedAt,
        sessionSummary: correctedSummary,
        costExposure: correctedSummary.costExposure,
        totalExposure: correctedSummary.exposure,
        exposure: correctedSummary.exposure,
        finalExposure: correctedSummary.finalExposure,
        flaggedCount: correctedSummary.flaggedCount,
        flaggedProducts: correctedSummary.flaggedProducts,
        completedLines: correctedSummary.completedLines,
        totalLines: correctedSummary.totalLines,
        correctionVersions: [...(Array.isArray(meta?.correctionVersions) ? meta.correctionVersions : []), revision],
      };
      const payload = { meta: nextMeta, lines, savedAt: submittedAt };
      const raw = JSON.stringify(payload);

      // Corrections are a canonical Count Ledger mutation. Sessions derives its
      // completed exposure/flags from mc-count-ledger:<sessionId>, so writing only
      // the legacy session-engine key leaves Sessions pinned to the pre-correction
      // summary (for example R0 while the corrected detail already shows R12).
      // Commit the corrected snapshot to the ledger immediately so every open
      // Sessions observer receives the same corrected exposure without refresh.
      mcSetCachedItem(countLedgerKeyFor(sessionId), raw);
      const correctionCommit = await mcTransitionCountLedger(
        countLedgerKeyFor(sessionId),
        raw,
        "correct",
        { name: correctionActorName, profileId: currentProfile?.id || currentProfile?.profileId || "" }
      );
      if (!correctionCommit?.ok) {
        // Never publish a corrected Sessions summary when Firestore has not yet
        // accepted the corrected canonical rows. That split-brain state was the
        // exact R0-in-Sessions / R400-on-reopen failure seen in Cashier.
        throw new Error(`Canonical correction commit failed: ${correctionCommit?.reason || "unknown"}`);
      }
      const committedCorrectionPayload = correctionCommit.payload || payload;
      const committedCorrectionRaw = correctionCommit.value || JSON.stringify(committedCorrectionPayload);
      mcSetCachedItem(countLedgerKeyFor(sessionId), committedCorrectionRaw);

      // Keep the legacy detail mirror current for older recovery/read paths. It is
      // compatibility only; the Count Ledger above remains the source of truth.
      mcSetCachedItem(storageKeyFor(sessionId), committedCorrectionRaw);
      mcSetCloudItem(storageKeyFor(sessionId), committedCorrectionRaw).catch(() => {});
      const committedMeta = committedCorrectionPayload?.meta || nextMeta;
      const committedLines = Array.isArray(committedCorrectionPayload?.lines) ? committedCorrectionPayload.lines : lines;
      upsertSessionRecord({
        ...committedMeta,
        id: sessionId,
        sessionId,
        store: nextMeta.store || nextMeta.storeName || "",
        storeName: nextMeta.storeName || nextMeta.store || "",
        totalLines: totals.totalLines,
        completedLines: totals.completedLines,
        totalExposure: totals.totalExposure,
        costExposure: totals.totalExposure,
        finalExposure: totals.totalExposure,
        exposure: totals.totalExposure,
        flaggedCount: totals.flaggedCount,
        flaggedProducts: totals.flaggedCount,
        sessionSummary: correctedSummary,
        updatedAt: submittedAt,
      });
      recordResponseCorrection(`response:${sessionId}`, {
        id: revision.id,
        submittedAt,
        startedAt: correctionStartedAt,
        correctedBy: correctionActorName,
        correctedByRole: correctionActorRole,
        reason,
        originalExposure: correctionOriginalSummary.exposure,
        currentExposure: totals.totalExposure,
        recoveredValue: correctionRecovery,
        flaggedProducts: totals.flaggedCount,
        changedProductCount: changedProducts.length,
        changedProducts,
        products: totals.rows.filter((row) => row.flagged).map((row) => ({
          productKey: row.id, product: row.product, sku: row.sku || "", category: row.category || "",
          systemSoh: Number(row.systemSoh || 0), counted: Number(row.counted || 0), waste: Number(row.waste || 0),
          unitCost: Number(row.cost || 0), variance: Number(row.variance || 0), exposure: Number(row.costVariance || 0),
        })),
      }, correctionActorName);
      setMeta(committedMeta);
      setLines(committedLines);
      setLastSavedAt(committedCorrectionPayload?.savedAt || submittedAt);
      setCorrectionMode(false);
      setCorrectionSubmitOpen(false);
      setOriginalCorrectionLines([]);
      setCorrectionReason("");
      setCorrectionOtherReason("");
      setCorrectionStartedAt(null);
      mcSetItem(correctionDraftKeyFor(sessionId), "");
      setCorrectionResult(revision);
      setSaveState("saved");
      hasUserEditedRef.current = false;
    } catch (error) {
      console.error("Correction submission failed", error);
      window.alert("Correction submission failed. Please try again.");
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const focusNextRowSameField = (currentLineId, fieldKey) => {
    const rowsInOrder = groupedFilteredRows.flatMap((group) => group.rows || []);
    const index = rowsInOrder.findIndex((row) => row.id === currentLineId);
    if (index < 0) return;
    const nextRow = rowsInOrder[index + 1];
    if (!nextRow) return;

    window.requestAnimationFrame(() => {
      const nextInput = fieldInputRefs.current?.[fieldKey]?.[nextRow.id];
      if (!nextInput) return;

      try {
        nextInput.focus({ preventScroll: true });
      } catch {
        nextInput.focus();
      }
      nextInput.select?.();

      const rect = nextInput.getBoundingClientRect?.();
      if (!rect) return;

      const topBuffer = 110;
      const bottomBuffer = Math.min(190, Math.max(120, window.innerHeight * 0.22));
      if (rect.bottom > window.innerHeight - bottomBuffer) {
        window.scrollBy({ top: rect.bottom - (window.innerHeight - bottomBuffer), behavior: "auto" });
      } else if (rect.top < topBuffer) {
        window.scrollBy({ top: rect.top - topBuffer, behavior: "auto" });
      }
    });
  };

  const handleCountFieldKeyDown = (event, line, key) => {
    if (isArchivedView || event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();
    const rawValue = event.currentTarget.value;
    const nextValue = key === "note" ? rawValue : evaluateCountExpression(rawValue);
    if (nextValue !== line[key]) {
      updateLine(line.id, key, nextValue);
    }
    if (quickFilter === "uncounted") {
      setHeldUncountedIds((current) => {
        const next = new Set(current);
        next.delete(line.id);
        return next;
      });
    }
    focusNextRowSameField(line.id, key);
  };

  const persistCurrentDetailSnapshot = async (statusOverride = null, options = {}) => {
    if (!meta || isArchivedView) return null;
    if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current);
    const now = new Date().toISOString();
    const canonicalSummary = {
      exposure: Number(totals.totalExposure || 0),
      costExposure: Number(totals.totalExposure || 0),
      finalExposure: Number(totals.totalExposure || 0),
      flaggedItems: Number(totals.flaggedCount || 0),
      flaggedCount: Number(totals.flaggedCount || 0),
      flaggedProducts: Number(totals.flaggedCount || 0),
      completedLines: Number(totals.completedLines || 0),
      totalLines: Number(totals.totalLines || 0),
      generatedAt: now,
    };
    const snapshotMeta = {
      ...meta,
      liveRevision: liveRevisionRef.current,
      status: statusOverride || meta.status || "Active",
      lastEditedBy: buildProfileDisplayName(currentProfileRef.current) || meta.lastEditedBy || meta.startedBy || "User",
      lastEditedAt: now,
      // Canonical session summary: once a count is completed every downstream
      // surface (Sessions, Archives, Response Designed and reports) reads this
      // frozen value instead of independently recalculating exposure.
      sessionSummary: canonicalSummary,
      costExposure: canonicalSummary.costExposure,
      totalExposure: canonicalSummary.exposure,
      exposure: canonicalSummary.exposure,
      finalExposure: canonicalSummary.finalExposure,
      flaggedCount: canonicalSummary.flaggedCount,
      flaggedProducts: canonicalSummary.flaggedProducts,
      completedLines: canonicalSummary.completedLines,
      totalLines: canonicalSummary.totalLines,
    };
    if (statusOverride === "Completed") snapshotMeta.completedAt = now;
    if (statusOverride === "Section Complete") {
      snapshotMeta.sectionCompletedAt = now;
      snapshotMeta.completedAt = snapshotMeta.completedAt || now;
    }
    let snapshotLines = lines;
    if (statusOverride === "Section Complete" || statusOverride === "Completed") {
      // Freeze the strongest row authority available at the instant of completion.
      // The current React render can race with a final autosave/cloud echo; the
      // checkpoint is edit-only and may contain the missing final keystrokes.
      const localPrimary = (() => { try { return JSON.parse(mcGetItem(storageKeyFor(sessionId)) || "null"); } catch { return null; } })();
      const localCheckpoint = (() => { try { return JSON.parse(mcGetItem(checkpointKeyFor(sessionId)) || "null"); } catch { return null; } })();
      const localLedger = (() => { try { return JSON.parse(mcGetItem(countLedgerKeyFor(sessionId)) || "null"); } catch { return null; } })();
      const currentPayload = { meta: snapshotMeta, lines, savedAt: now };
      const strongest = composeCompletedAuthority(currentPayload, localLedger, localPrimary, localCheckpoint);
      if (strongest?.lines?.length && completedPayloadTouchedCount(strongest) >= completedPayloadTouchedCount(currentPayload)) {
        snapshotLines = strongest.lines;
      }
      // MC413: terminal truth must be calculated from the exact rows that are
      // being frozen, not from the React `totals` render that existed when the
      // operator first clicked Complete. During a hard/fast count the save queue
      // can still be catching up, so that render may legitimately show an older
      // exposure/flag summary even though the strongest local ledger already has
      // all final values. Recalculate the canonical summary from snapshotLines so
      // Sessions, Response Designed and WhatsApp all receive the same final truth.
      let frozenExposure = 0;
      let frozenFlags = 0;
      let frozenTouched = 0;
      (snapshotLines || []).forEach((line) => {
        const touched = line.counted !== "" || line.waste !== "" || String(line.note || "").trim();
        if (!touched) return;
        frozenTouched += 1;
        const countedValue = liveCountNumber(line.counted);
        const wasteValue = liveCountNumber(line.waste);
        const unitCost = resolveLineUnitCost(line, snapshotMeta, activeStoreScopeRef.current, unitEngineConfig);
        const variance = roundTo2(Number(countedValue || 0) + Number(wasteValue || 0) - Number(line.systemSoh || 0));
        const costVariance = roundTo2(Number(variance || 0) * Number(unitCost.cost || 0));
        const noteFlagged = Boolean(String(line.note || "").trim());
        const { negativeFlagged, positiveFlagged } = resolveCostVarianceFlagState(costVariance, flagConfig);
        if (noteFlagged || negativeFlagged || positiveFlagged) frozenFlags += 1;
        frozenExposure += Number(costVariance || 0);
      });
      frozenExposure = roundTo2(frozenExposure);
      snapshotMeta.sessionSummary = {
        ...snapshotMeta.sessionSummary,
        exposure: frozenExposure,
        costExposure: frozenExposure,
        finalExposure: frozenExposure,
        flaggedItems: frozenFlags,
        flaggedCount: frozenFlags,
        flaggedProducts: frozenFlags,
        completedLines: frozenTouched,
        totalLines: (snapshotLines || []).length,
        generatedAt: now,
      };
      snapshotMeta.costExposure = frozenExposure;
      snapshotMeta.totalExposure = frozenExposure;
      snapshotMeta.exposure = frozenExposure;
      snapshotMeta.finalExposure = frozenExposure;
      snapshotMeta.flaggedCount = frozenFlags;
      snapshotMeta.flaggedProducts = frozenFlags;
      snapshotMeta.completedLines = frozenTouched;
      snapshotMeta.totalLines = (snapshotLines || []).length;
    }
    const payload = { meta: snapshotMeta, lines: snapshotLines, savedAt: now };
    const raw = JSON.stringify(payload);
    setSaveState("saving");
    // Lifecycle transitions must never race a still-running Active autosave.
    // Queue the exact latest snapshot and wait for the background worker to drain
    // before Complete/Finalize freezes the canonical ledger.
    if (statusOverride === "Section Complete" || statusOverride === "Completed") {
      lastQueuedEditSequenceRef.current = localEditSequenceRef.current;
      editsSinceCloudCheckpointRef.current = 0;
      checkpointWindowStartedAtRef.current = 0;
      enqueueLiveCountSave({
        sessionId,
        cloudKey: countLedgerKeyFor(sessionId),
        raw: JSON.stringify({ ...payload, meta: { ...snapshotMeta, status: meta.status || "Active", sectionCompletedAt: undefined, completedAt: undefined } }),
        touchedCount: countTouchedRows(snapshotLines),
        totalCount: snapshotLines.length,
        orgId: getMcActiveOrgId(),
      });
      const drained = await drainLiveCountSaveQueue(sessionId, { timeoutMs: 600000 });
      if (!drained) throw new Error("Live count is still saving. Morpheus kept the completion request open for 10 minutes but the cloud did not acknowledge the queue.");
    }
    mcSetCachedItem(countLedgerKeyFor(sessionId), raw);
    mcSetCachedItem(storageKeyFor(sessionId), raw);
    mcSetCachedItem(checkpointKeyFor(sessionId), raw);
    const terminalSnapshot = Boolean(snapshotMeta.sectionCompletedAt || snapshotMeta.completedAt || ["Section Complete", "Completed"].includes(snapshotMeta.status));
    if (terminalSnapshot) mcSetCachedItem(completedSnapshotKeyFor(sessionId), raw);
    const awaitCloud = options?.awaitCloud !== false;
    let canonicalPayload = payload;
    let canonicalRaw = raw;
    let cloudSave;
    if (terminalSnapshot) {
      // Phase 3: completion/finalization is a Firestore transaction on the canonical
      // Count Ledger. Compatibility documents are written only AFTER that transition
      // succeeds, and always mirror the exact frozen canonical payload.
      const transition = statusOverride === "Completed" ? "finalize" : "complete";
      cloudSave = (async () => {
        let result = null;
        const actor = {
          name: buildProfileDisplayName(currentProfileRef.current) || snapshotMeta.lastEditedBy || snapshotMeta.startedBy || "User",
          profileId: currentProfileRef.current?.id || "",
        };
        // Slow networks can finish the queued Active save a moment before Firestore
        // exposes that exact revision to the lifecycle transaction. Keep the original
        // Complete intent alive and retry the terminal transition instead of dropping
        // the operator back onto a second Complete button.
        for (let attempt = 0; attempt < 5; attempt += 1) {
          result = await mcTransitionCountLedger(countLedgerKeyFor(sessionId), raw, transition, actor);
          if (result?.ok && result?.payload) break;
          if (attempt < 4) await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
        }
        if (!result?.ok || !result?.payload) throw new Error(`Canonical ledger ${transition} failed: ${result?.reason || "unknown"}`);
        canonicalPayload = result.payload;
        canonicalRaw = result.value || JSON.stringify(result.payload);
        mcSetCachedItem(countLedgerKeyFor(sessionId), canonicalRaw);
        mcSetCachedItem(storageKeyFor(sessionId), canonicalRaw);
        mcSetCachedItem(checkpointKeyFor(sessionId), canonicalRaw);
        mcSetCachedItem(completedSnapshotKeyFor(sessionId), canonicalRaw);
        const terminalMirrorWrites = await Promise.all([
          mcSetCloudItemImmediate(storageKeyFor(sessionId), canonicalRaw),
          mcSetCloudItemImmediate(checkpointKeyFor(sessionId), canonicalRaw),
          mcSetCloudItemImmediate(completedSnapshotKeyFor(sessionId), canonicalRaw),
        ]);
        if (terminalMirrorWrites.some((saved) => saved !== true)) {
          throw new Error(
            "The canonical count completed, but Morpheus could not confirm all terminal recovery snapshots in Firestore."
          );
        }
        return result;
      })();
    } else {
      cloudSave = Promise.all([
        mcSetCloudItem(countLedgerKeyFor(sessionId), raw),
        mcSetCloudItem(storageKeyFor(sessionId), raw),
        mcSetCloudItem(checkpointKeyFor(sessionId), raw),
      ]);
    }
    try {
      if (awaitCloud) await cloudSave;
    } catch (err) {
      console.warn("MC detail cloud save failed", err);
      setSaveState("saved");
      throw err;
    }
    const committedMeta = terminalSnapshot ? (canonicalPayload?.meta || snapshotMeta) : snapshotMeta;
    setMeta(committedMeta);
    lastSavedAtRef.current = now;
    setLastSavedAt(now);
    setSaveState("saved");
    hasUserEditedRef.current = false;
    editsSinceCloudCheckpointRef.current = 0;
    checkpointWindowStartedAtRef.current = 0;
    lastQueuedEditSequenceRef.current = localEditSequenceRef.current;
    clearLiveSaveOutbox(sessionId);
    setSaveProgress({
      savedCount: countTouchedRows(terminalSnapshot ? canonicalPayload?.lines : payload.lines),
      targetCount: countTouchedRows(terminalSnapshot ? canonicalPayload?.lines : payload.lines),
      totalCount: (terminalSnapshot ? canonicalPayload?.lines : payload.lines)?.length || 0,
      pending: false,
    });
    return terminalSnapshot ? canonicalPayload : payload;
  };

  const saveNow = () => {
    if (correctionMode) {
      const now = new Date().toISOString();
      mcSetItem(correctionDraftKeyFor(sessionId), JSON.stringify({
        meta,
        lines,
        savedAt: now,
        correctionReason,
        correctionOtherReason,
        correctionStartedAt,
        originalLines: originalCorrectionLines,
      }));
      setSaveState("saved");
      hasUserEditedRef.current = false;
      return;
    }
    persistCurrentDetailSnapshot();
  };

  const archiveCurrentSession = async () => {
    if (!isAdminProfile(currentProfile) || archiveBusy) return;
    setArchiveBusy(true);
    try {
      const snapshot = await persistCurrentDetailSnapshot("Active");
      await archiveSessionRecord(sessionId, "Archived from live count", snapshot);
      navigate("/morpheus-count/archive");
    } finally {
      setArchiveBusy(false);
    }
  };

  const finalizeCurrentSession = async () => {
    if (!canFinalizeCount(currentProfile) || finalizeBusy) return;
    setFinalizeBusy(true);
    try {
      const snapshot = await persistCurrentDetailSnapshot("Completed");
      await archiveSessionRecord(sessionId, "Finalized completed count", snapshot);
      navigate("/morpheus-count/archive");
    } finally {
      setFinalizeBusy(false);
    }
  };

  const discoverPreviousComparableCount = async (currentRecord) => {
    if (!currentRecord?.sessionId) return null;
    const currentSite = comparisonSiteKey(currentRecord);
    const currentSection = comparisonSectionKey(currentRecord);
    if (!currentSite || !currentSection) return null;

    // Operational comparison priority:
    // 1. the latest completed same-site/same-section count still in Sessions;
    // 2. only when none exists, the latest finalized historical Archive count.
    const currentId = String(currentRecord.sessionId || "");

    const completedSessionCandidates = loadSessionRecords()
      .filter((record) => {
        const id = String(record?.sessionId || "").trim();
        if (!id || id === currentId) return false;
        const status = String(record?.status || "").trim().toLowerCase();
        return Boolean(
          record?.sectionCompletedAt
          || record?.meta?.sectionCompletedAt
          || status === "section complete"
          || status === "section-complete"
        );
      })
      .filter((record) => comparisonSiteKey(record) === currentSite && comparisonSectionKey(record) === currentSection)
      .sort((a, b) => comparisonRecordTime(b) - comparisonRecordTime(a))
      .slice(0, 30);

    let previous = completedSessionCandidates[0] || null;

    if (!previous) {
      const finalizedArchiveCandidates = loadArchivedSessionRecords()
        .filter(isFinalizedComparisonAuthority)
        .filter((record) => String(record?.sessionId || "").trim() !== currentId)
        .filter((record) => comparisonSiteKey(record) === currentSite && comparisonSectionKey(record) === currentSection)
        .sort((a, b) => comparisonRecordTime(b) - comparisonRecordTime(a))
        .slice(0, 30);

      previous = finalizedArchiveCandidates[0] || null;
    }

    if (!previous) return null;

    // Archived list rows are compact by design. Pull the immutable final snapshot
    // for the one selected historical candidate so the completion prompt uses the
    // actual frozen exposure/flag summary instead of treating a compact row as R0.
    if (isFinalizedComparisonAuthority(previous) && (previous?.archivedAt || String(previous?.status || "").trim().toLowerCase() === "archived")) {
      try {
        const finalRaw = await mcGetCloudItem(finalSnapshotKeyFor(previous.sessionId));
        if (finalRaw) {
          const finalPayload = JSON.parse(finalRaw);
          if (finalPayload && typeof finalPayload === "object") {
            previous = {
              ...previous,
              ...finalPayload,
              meta: { ...(previous?.meta || {}), ...(finalPayload?.meta || {}) },
              sessionSummary: finalPayload?.meta?.sessionSummary || previous?.sessionSummary,
              lines: Array.isArray(finalPayload?.lines) ? finalPayload.lines : previous?.lines,
            };
          }
        }
      } catch (_) {
        // Keep the compact archive row as fallback; detailed Compare will still
        // hydrate its own immutable/canonical row authorities.
      }
    }

    const previousExposure = comparisonExposure(previous);
    const currentExposure = comparisonExposure(currentRecord);
    const previousFlags = comparisonFlags(previous);
    const currentFlags = comparisonFlags(currentRecord);
    const exposureDelta = currentExposure - previousExposure;
    const flagDelta = currentFlags - previousFlags;

    // CI4 prompt appears only when something materially changed. The detailed CI1
    // engine remains the authority once the manager opens Compare.
    if (Math.abs(exposureDelta) < 0.005 && flagDelta === 0) return null;

    return {
      previous,
      current: currentRecord,
      previousExposure,
      currentExposure,
      exposureDelta,
      previousFlags,
      currentFlags,
      flagDelta,
      previousActor: comparisonActor(previous),
      previousCycle: comparisonCycle(previous),
      previousTime: comparisonRecordTime(previous),
    };
  };

  const openCompletionComparison = () => {
    if (!completionComparison?.previous?.sessionId || !completionComparison?.current?.sessionId) return;
    const ids = [completionComparison.previous.sessionId, completionComparison.current.sessionId].join(",");
    setCompletionComparison(null);
    navigate(`/morpheus-count/archive?compare=1&ids=${encodeURIComponent(ids)}&source=active`);
  };

  const completeSection = async () => {
    if (isArchivedView || !isComplete || finalizeBusy) return;
    setFinalizeBusy(true);
    setCompletionComparisonLoading(false);
    setCompletionResponse(null);
    setCompletionComparison(null);
    setCompletionError("");
    try {
      const snapshot = await persistCurrentDetailSnapshot("Section Complete", { awaitCloud: true });
      const committedSummary = snapshot?.meta?.sessionSummary || {
        exposure: totals.totalExposure,
        costExposure: totals.totalExposure,
        finalExposure: totals.totalExposure,
        flaggedItems: totals.flaggedCount,
        flaggedCount: totals.flaggedCount,
        flaggedProducts: totals.flaggedCount,
        completedLines: totals.completedLines,
        totalLines: totals.totalLines,
      };
      const committedExposure = Number(committedSummary.exposure ?? committedSummary.costExposure ?? committedSummary.finalExposure ?? 0);
      const committedFlags = Number(committedSummary.flaggedCount ?? committedSummary.flaggedItems ?? committedSummary.flaggedProducts ?? 0);
      const sessionRecord = {
        ...(meta || {}),
        ...(snapshot?.meta || {}),
        id: sessionId,
        sessionId,
        store: snapshot?.meta?.store || meta?.store || "",
        storeName: snapshot?.meta?.store || meta?.store || "",
        completedLines: Number(committedSummary.completedLines || 0),
        totalLines: Number(committedSummary.totalLines || snapshot?.lines?.length || totals.totalLines || 0),
        totalExposure: committedExposure,
        costExposure: committedExposure,
        finalExposure: committedExposure,
        exposure: committedExposure,
        flaggedCount: committedFlags,
        flaggedProducts: committedFlags,
        sessionSummary: committedSummary,
        status: "Section Complete",
      };

      // Persist the canonical summary immediately. Do not wait for a React
      // effect or a later hydration pass: the Sessions list may receive a lean
      // Firestore update in the meantime and would otherwise display R0.
      upsertSessionRecord(sessionRecord);

      let responseEvent = null;
      let eventNotifications = [];
      if (committedFlags > 0 || Math.abs(committedExposure) > 0) {
        // P4.3: snapshot is the Firestore-acknowledged terminal Count Ledger payload.
        // Response Intelligence may consume that frozen revision only.
        responseEvent = buildResponseEventFromSession(sessionRecord, snapshot);
        if (responseEvent) {
          // appendResponseEvent creates the queue and starts delivery asynchronously.
          // Count completion must never wait for external email or WhatsApp providers.
          appendResponseEvent(responseEvent);
          eventNotifications = getNotificationsForEvent(responseEvent.id);
        }
      }

      const emailNotifications = eventNotifications.filter((item) => item?.channel === "email");
      const whatsappNotifications = eventNotifications.filter((item) => item?.channel === "whatsapp");
      const emailQueued = emailNotifications.filter((item) => ["queued", "sending"].includes(String(item?.status || "").toLowerCase()));
      const whatsappQueued = whatsappNotifications.filter((item) => ["queued", "sending"].includes(String(item?.status || "").toLowerCase()));
      const whatsappSkipped = whatsappNotifications.filter((item) => String(item?.status || "").toLowerCase() === "skipped");

      setCompletionResponse({
        exposure: committedExposure,
        flagged: committedFlags,
        delivered: 0,
        failed: 0,
        recipients: emailNotifications.map((item) => ({
          name: item.recipientName || item.recipient,
          role: item.recipientRole || "Recipient",
          email: item.recipient,
        })).filter((item) => item.email),
        emailQueued: emailQueued.length,
        whatsappQueued: whatsappQueued.length,
        whatsappSkipped: whatsappSkipped.length,
        whatsappSkipReason: whatsappSkipped[0]?.statusDetail || whatsappSkipped[0]?.reason || "No WhatsApp number configured for the selected responder.",
        whatsappSent: 0,
        whatsappPrepared: 0,
        whatsappFailed: 0,
        whatsappRecipients: whatsappNotifications.map((item) => ({
          name: item.recipientName || item.recipient,
          role: item.recipientRole || "Recipient",
          phone: item.recipient,
        })).filter((item) => item.phone),
        hadVariance: committedFlags > 0 || Math.abs(committedExposure) > 0,
        responseEventId: responseEvent?.id || "",
      });

      // CI4: terminal completion is now finished.
      // First release the existing Complete loader and let the completed/locked
      // count screen actually paint. Only after that do we hand off to the
      // comparison lookup overlay.
      setFinalizeBusy(false);

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });

      setCompletionComparisonLoading(true);

      // Guarantee one painted comparison-loading frame, but do not artificially
      // hold the spinner. As soon as the comparison authority is resolved, show
      // the comparison modal immediately.
      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const nextComparison = await discoverPreviousComparableCount(sessionRecord);

      setCompletionComparison(nextComparison);
      setCompletionComparisonLoading(false);
    } catch (error) {
      console.error("Morpheus count completion failed", error);
      setCompletionComparisonLoading(false);
      setCompletionError(error?.message || "Count completion could not be confirmed. Your saved count remains intact.");
    } finally {
      setFinalizeBusy(false);
    }
  };

  const canArchiveSession = isAdminProfile(currentProfile) && !isArchivedView;

  const exportPdf = async () => {
    try {
      const isFullSiteCount = meta?.fullSite === true || String(meta?.countScope || meta?.sessionType || "").toLowerCase().includes("full");
      const resolvedSection = isFullSiteCount
        ? { sectionName: "Full Count" }
        : resolveSectionFromCountLines(lines, meta);
      const sectionName = resolvedSection.sectionName || meta.sectionName || "";

      await exportSessionCountPdf({
        session: {
          id: meta.sessionId,
          sessionId: meta.sessionId,
          store: meta.store,
          storeName: meta.store,
          sectionId: resolvedSection.sectionId || meta.sectionId || "",
          sectionName,
          startedBy: meta.startedBy,
          lastEditedBy: meta.lastEditedBy || meta.startedBy,
          completedAt: meta.completedAt || meta.archivedAt || meta.lastEditedAt || lastSavedAt,
          updatedAt: meta.lastEditedAt || lastSavedAt || meta.startedAt,
        },
        rows: totals.rows,
        summaryData: {
          totalLoss: totals.rows.reduce((sum, row) => (Number(row.costVariance || 0) < 0 ? sum + Number(row.costVariance || 0) : sum), 0),
          totalGain: totals.rows.reduce((sum, row) => (Number(row.costVariance || 0) > 0 ? sum + Number(row.costVariance || 0) : sum), 0),
          netDifference: totals.totalExposure,
          flaggedItems: totals.flaggedCount,
          countedItems: totals.completedLines,
          cleanItems: totals.rows.filter((row) => row.touched && !row.flagged).length,
          noteItems: totals.rows.filter((row) => String(row.note || row.notes || "").trim()).length,
        },
      });
    } catch (error) {
      console.error(error);
      window.alert("PDF export failed. Please try again.");
    }
  };


  const orderRowsFromCount = () => totals.rows.map((line) => ({
    id: line.id,
    product: line.product,
    category: line.category || "—",
    sku: line.sku || "—",
    counted: formatNumber(line.counted),
    order: "",
    orderNote: "",
  }));

  const openOrderSheetModal = () => {
    setOrderSheets(loadOrderSheets());
    setOrderDraft(orderRowsFromCount());
    setSelectedOrderSheet(null);
    setOrderModalOpen(true);
  };

  const currentProfileName = buildProfileDisplayName(currentProfile) || "User";
  const visibleOrderSheets = orderSheets.filter((sheet) => {
    if (!sheet) return false;
    const admin = isAdminProfile(currentProfile);
    const selectedSite = adminStoreView || "ALL";
    if (admin) {
      if (selectedSite === "ALL" || !selectedSite) return true;
      return normalizeStoreValue(sheet.storeKey || sheet.storeName || sheet.store || sheet.site) === normalizeStoreValue(selectedSite);
    }
    return filterRecordsForProfile([{ storeKey: sheet.storeKey, storeName: sheet.storeName, store: sheet.storeName, site: sheet.storeName }], currentProfile).length > 0;
  });

  const saveOrderSheet = async (shouldExport = false) => {
    const now = new Date().toISOString();
    const normalizedRows = orderDraft.map((row) => ({
      ...row,
      order: String(row.order ?? "").trim() ? row.order : "0",
    }));
    const sheet = {
      id: `MOS-${sessionId}-${Date.now()}`,
      sessionId: meta?.sessionId || sessionId,
      storeName: meta?.store || activeStoreScope || "All Sites",
      storeKey: meta?.storeKey || activeStoreScope || "ALL",
      startedBy: meta?.startedBy || "User",
      createdAt: now,
      createdBy: currentProfileName,
      createdByKey: profileKey(currentProfile),
      rows: normalizedRows,
    };
    const next = [sheet, ...loadOrderSheets()];
    saveOrderSheets(next);
    setOrderSheets(next);
    setSelectedOrderSheet(sheet);
    if (shouldExport) {
      const exportRows = filterMosRowsForExport(sheet.rows, mosPdfConfig);
      await exportMorpheusOrderSheetPdf({ sheet, session: meta || {}, rows: exportRows, options: mosPdfConfig });
    }
  };

  const focusNextOrderRow = (index, field = "order") => {
    const nextKey = `${field}-${index + 1}`;
    const fallbackKey = `order-${index + 1}`;
    window.requestAnimationFrame(() => {
      const next = orderInputRefs.current[nextKey] || orderInputRefs.current[fallbackKey];
      if (next) {
        next.focus();
        next.select?.();
      }
    });
  };

  const handleOrderKeyDown = (event, index, field = "order") => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    focusNextOrderRow(index, field);
  };

  if (!meta) {
    return (
      <div className="mc-detail">
        <div className="mc-detail__grid" />
        <div className="mc-detail__ambient mc-detail__ambient--one" />
        <div className="mc-detail__ambient mc-detail__ambient--two" />
        <header className="mc-detail__header">
          <div>
            <div className="mc-detail__eyebrow">Live count engine</div>
            <h1>Opening Live Count</h1>
            <p>{sessionId} • loading session metadata and count lines…</p>
          </div>
          <div className="mc-detail__actions mc-pageControlsOffset">
            <Link className="mc-detail__btn" to="/morpheus-count/sessions">Back to Sessions</Link>
          </div>
        </header>
      </div>
    );
  }

  const exposureClass = totals.totalExposure > 0 ? "positive" : totals.totalExposure < 0 ? "negative" : "neutral";
  const isComplete = totals.totalLines > 0 && totals.completedLines >= totals.totalLines;
  const sectionCompleted = Boolean(meta.sectionCompletedAt || meta.status === "Section Complete");
  const canFinalize = canFinalizeCount(currentProfile);

  const archiveLifecycleEvents = (() => {
    if (!isArchivedView) return [];
    const events = [];
    const addEvent = (key, label, at, actor, detail, tone = "complete") => {
      if (!at) return;
      const parsed = new Date(at);
      if (Number.isNaN(parsed.getTime())) return;
      events.push({ key, label, at: parsed.toISOString(), actor: actor || "System", detail, tone });
    };
    addEvent("prepared", "Count prepared", meta.preparedAt || meta.createdAt, meta.preparedBy || meta.startedBy, `${meta.importedRows || totals.totalLines} count lines prepared for ${meta.store || "this site"}. Category scope: ${sessionCategoryScopeLabel(meta)}.`);
    addEvent("started", "Counting started", meta.startedAt, meta.startedBy, `${meta.sectionName || (meta.fullSite ? "Full Count" : "Section")} opened in ${meta.cycleName || "the assigned cycle"}.`);
    addEvent("completed", "Section completed", meta.sectionCompletedAt || meta.completedAt, meta.completedBy || meta.lastEditedBy || meta.startedBy, `${totals.completedLines} of ${totals.totalLines} lines completed with ${totals.flaggedCount} flagged item${totals.flaggedCount === 1 ? "" : "s"}.`);
    (Array.isArray(meta.correctionVersions) ? meta.correctionVersions : []).forEach((revision, index) => {
      addEvent(`correction-${revision?.id || index}`, `Correction version ${revision?.version || index + 2}`, revision?.submittedAt || revision?.correctedAt, revision?.correctedBy, `${revision?.changedProducts?.length || revision?.changedProductCount || 0} product${(revision?.changedProducts?.length || revision?.changedProductCount || 0) === 1 ? "" : "s"} changed${revision?.reason ? ` · ${revision.reason}` : ""}.`, "correction");
    });
    addEvent("verified", "Count verified", meta.verifiedAt, meta.verifiedBy, "The completed section was reviewed and approved for finalization.", "verified");
    addEvent("finalized", "Count finalized", meta.finalizedAt, meta.finalizedBy || meta.verifiedBy, "The count was locked as the authoritative operational result.", "finalized");
    addEvent("archived", "Count archived", meta.archivedAt || lastSavedAt, meta.archivedBy || meta.lastEditedBy, "The finalized count moved into historical reporting.", "archived");

    // MC408 — restore history is audit history, not snapshot history. The immutable
    // final-count payload can pre-date a restore, so reconstruct every restore from
    // the archive projection as well as lifecycleEvents. This keeps actor + reason.
    (Array.isArray(meta.restoreAudit) ? meta.restoreAudit : []).forEach((event, index) => {
      addEvent(
        `restore-audit-${event?.id || index}`,
        "Count restored",
        event?.at || event?.timestamp || event?.restoredAt,
        event?.actor || event?.by || event?.restoredBy || meta.restoredBy,
        `The archived count was restored to Sessions${event?.reason || meta.restoreReason ? ` · Reason: ${event?.reason || meta.restoreReason}` : ""}.`,
        "restored"
      );
    });
    if (!(Array.isArray(meta.restoreAudit) && meta.restoreAudit.length)) {
      addEvent("restored", "Count restored", meta.restoredAt, meta.restoredBy, `The archived count was restored to Sessions${meta.restoreReason ? ` · Reason: ${meta.restoreReason}` : ""}.`, "restored");
    }

    const latestRestoreAt = [...(Array.isArray(meta.restoreAudit) ? meta.restoreAudit : []), ...(Array.isArray(meta.lifecycleEvents) ? meta.lifecycleEvents.filter((event) => String(event?.type || "").toLowerCase() === "restored") : [])]
      .map((event) => new Date(event?.at || event?.timestamp || event?.restoredAt || 0).getTime())
      .filter(Number.isFinite)
      .reduce((max, value) => Math.max(max, value), 0);

    (Array.isArray(meta.lifecycleEvents) ? meta.lifecycleEvents : []).forEach((event, index) => {
      const type = String(event?.type || "").toLowerCase();
      const eventAtMs = new Date(event?.at || event?.timestamp || 0).getTime();
      const labels = {
        finalized: latestRestoreAt && Number.isFinite(eventAtMs) && eventAtMs > latestRestoreAt ? "Count re-finalized" : "Count finalized",
        restored: "Count restored",
        verified: latestRestoreAt && Number.isFinite(eventAtMs) && eventAtMs > latestRestoreAt ? "Count re-verified" : "Count verified",
        archived: "Count archived",
      };
      if (!labels[type]) return;
      addEvent(
        `audit-${event?.id || `${type}-${index}`}`,
        labels[type],
        event?.at || event?.timestamp,
        event?.actor || event?.by,
        event?.detail || (type === "restored"
          ? `The archived record was intentionally restored to Sessions${event?.reason ? ` · ${event.reason}` : ""}.`
          : type === "finalized"
            ? "The count was locked as the authoritative operational result."
            : type === "verified"
              ? "The completed section was reviewed and approved for finalization."
              : "The finalized count moved into historical reporting."),
        type
      );
    });

    const deduped = [];
    const seen = new Set();
    events.sort((a, b) => new Date(a.at) - new Date(b.at)).forEach((event) => {
      const key = `${event.label}|${event.at}|${event.actor}`;
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(event);
    });
    return deduped;
  })();

  const trafficKeyRows = Object.values(trafficStats.byKey || {})
    .map((row) => ({
      ...row,
      estimatedTotalDocumentBytes: Number(row?.estimatedDocumentWriteBytes || 0) + Number(row?.estimatedDocumentReadBytes || 0),
    }))
    .sort((a, b) => Number(b.estimatedTotalDocumentBytes || 0) - Number(a.estimatedTotalDocumentBytes || 0));

  return (
    <div id="mc-pdf-summary" className={`mc-detail ${isArchivedView && archiveDetailTab === "timeline" ? "is-archive-timeline" : ""}`}>
      <div className="mc-detail__grid" />
      <div className="mc-detail__ambient mc-detail__ambient--one" />
      <div className="mc-detail__ambient mc-detail__ambient--two" />

      <header className="mc-detail__header">
        <div>
          <div className="mc-detail__eyebrow">Live count engine</div>
          {(() => {
            const isFullSiteTitle = meta?.fullSite === true || String(meta?.countScope || meta?.sessionType || '').toLowerCase().includes('full');
            const resolvedSection = isFullSiteTitle ? { sectionName: 'Full Count' } : resolveSectionFromCountLines(lines, meta);
            const title = resolvedSection.sectionName || meta.sectionName || "Live Count";
            return (
              <>
                <h1>{title}</h1>
                <p>
                  {meta.store} • {meta.sessionId} • {meta.importedRows || totals.totalLines} items loaded • Started by {meta.startedBy}
                </p>
                <p className="mc-session-category-line"><strong>Categories:</strong> {sessionCategoryScopeLabel(meta)}
                </p>
              </>
            );
          })()}
        </div>

        <div className="mc-detail__actions mc-pageControlsOffset">
          {trafficProbeEnabled && !isArchivedView ? (
            <button
              className={`mc-detail__btn mc-detail__btn--ghost mc-detail__trafficToggle ${trafficOpen ? "is-active" : ""}`}
              type="button"
              onClick={() => {
                if (!trafficOpen) setTrafficStats(getMcTrafficProbeSnapshot(sessionId));
                setTrafficOpen((current) => !current);
              }}
              title="Local development traffic inspector — adds no Firestore reads or writes"
            >
              Dev Traffic · {formatTrafficBytes(trafficStats.serverPathEstimatedDocumentBytes || trafficStats.totalObservedBytes)} est
            </button>
          ) : null}
          {!isArchivedView && isComplete && !sectionCompleted ? (
            <button
              className="mc-detail__btn mc-detail__btn--primary"
              type="button"
              onClick={completeSection}
              disabled={finalizeBusy}
              title="Mark this section as completed. The site count can still be finalized later by admin."
            >
              {finalizeBusy && saveState === "saving" ? `Saving ${Number(saveProgress.savedCount || 0)} / ${Number(saveProgress.targetCount || totals.completedLines || totals.totalLines)}…` : finalizeBusy ? "Completing…" : "Complete Section"}
            </button>
          ) : null}
          {!isArchivedView && sectionCompleted && !correctionMode ? (
            <>
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={exportPdf}>
                Export Count PDF
              </button>
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={openOrderSheetModal}>
                Order Sheet
              </button>
              <button className="mc-detail__btn mc-detail__btn--primary" type="button" onClick={() => setCorrectionInfoOpen(true)}>
                Review &amp; Correct Count
              </button>
            </>
          ) : null}
          {isArchivedView ? (
            <>
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={exportPdf}>
                Export Count PDF
              </button>
              {isComplete ? (
                <button className="mc-detail__btn mc-detail__btn--primary" type="button" onClick={openOrderSheetModal}>
                  Order Sheet
                </button>
              ) : null}
            </>
          ) : null}
          {!isArchivedView && !isComplete && canArchiveSession ? (
            <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={() => setConfirmArchive(true)}>
              Archive Session
            </button>
          ) : null}
          <Link
            className="mc-detail__btn mc-detail__btn--ghost"
            to={isArchivedView ? "/morpheus-count/archive" : "/morpheus-count/sessions"}
          >
            {isArchivedView ? "Back to Archives" : "Back to Sessions"}
          </Link>
        </div>
      </header>

      {isArchivedView ? (
        <>
          <nav className="mc-detail__archiveTabs" aria-label="Archived count detail views">
            <button type="button" className={archiveDetailTab === "overview" ? "is-active" : ""} onClick={() => setArchiveDetailTab("overview")}>Overview</button>
            <button type="button" className={archiveDetailTab === "timeline" ? "is-active" : ""} onClick={() => setArchiveDetailTab("timeline")}>Lifecycle Timeline</button>
          </nav>
          {archiveDetailTab === "timeline" ? (
            <section className="mc-detail__archiveTimelinePanel" aria-labelledby="mc-archive-timeline-title">
              <div className="mc-detail__archiveTimelineHead">
                <div>
                  <span>Audit history</span>
                  <h2 id="mc-archive-timeline-title">Lifecycle Timeline</h2>
                  <p>Prepared, counted, corrected, verified, finalized and archived events for this section.</p>
                </div>
                <strong>{archiveLifecycleEvents.length} recorded event{archiveLifecycleEvents.length === 1 ? "" : "s"}</strong>
              </div>
              {archiveLifecycleEvents.length ? (
                <div className="mc-detail__archiveTimeline">
                  {archiveLifecycleEvents.map((event, index) => (
                    <article className={`mc-detail__archiveTimelineEvent is-${event.tone}`} key={event.key}>
                      <div className="mc-detail__archiveTimelineRail" aria-hidden="true"><span>{index + 1}</span></div>
                      <div className="mc-detail__archiveTimelineContent">
                        <div className="mc-detail__archiveTimelineTitle">
                          <h3>{event.label}</h3>
                          <time dateTime={event.at}>{new Date(event.at).toLocaleString()}</time>
                        </div>
                        <p>{event.detail}</p>
                        <small>By <strong>{event.actor}</strong></small>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="mc-detail__archiveTimelineEmpty">
                  <strong>No lifecycle timestamps were captured for this legacy count.</strong>
                  <p>The final archived count remains available in Overview. New records will build this timeline automatically.</p>
                </div>
              )}
            </section>
          ) : null}
        </>
      ) : null}


      {!isArchivedView && meta?.restoredFromArchive ? (
        <section className="mc-detail__restoreBanner" role="status" aria-label="Restored session information">
          <div className="mc-detail__restoreBannerIcon" aria-hidden="true">↺</div>
          <div className="mc-detail__restoreBannerBody">
            <div className="mc-detail__restoreBannerEyebrow">Restored from archive</div>
            <h2>This count was intentionally returned to the active Sessions workspace.</h2>
            <div className="mc-detail__restoreBannerMeta">
              <span><small>Reason</small><strong>{meta.restoreReason || "Not recorded"}</strong></span>
              <span><small>Restored by</small><strong>{meta.restoredBy || meta.restoreRequestedBy || "System"}</strong></span>
              <span><small>Restored</small><strong>{meta.restoredAt ? new Date(meta.restoredAt).toLocaleString() : "Time not recorded"}</strong></span>
              <span><small>Mode</small><strong>{String(meta.restoreMode || "new").toLowerCase() === "replace" ? "Replaced existing session" : "New active session"}</strong></span>
            </div>
            <p>This banner remains visible while the restored count is active. Any changes, verification, finalization and re-archive events will remain in the Lifecycle Timeline.</p>
          </div>
        </section>
      ) : null}

      {!isArchivedView && sectionCompleted && !correctionMode ? (
        <section className="mc-detail__lockedBanner" role="status">
          <div className="mc-detail__lockedIcon" aria-hidden="true">✓</div>
          <div>
            <div className="mc-detail__lockedEyebrow">Count locked</div>
            <h2>This count has been completed and can no longer be edited directly.</h2>
            <p>Use <strong>Review &amp; Correct Count</strong> to make an audited correction without changing the original completed count.</p>
          </div>
        </section>
      ) : null}

      {!isArchivedView && correctionMode ? (
        <section className="mc-detail__correctionBanner" role="status">
          <div>
            <div className="mc-detail__correctionEyebrow">Correction mode</div>
            <h2>Reviewing the completed count without changing the original record.</h2>
            <p><strong>Reason:</strong> {correctionReason === "Other" ? correctionOtherReason : correctionReason} · Started {correctionStartedAt ? new Date(correctionStartedAt).toLocaleString() : "now"}</p>
          </div>
          <div className="mc-detail__correctionBannerActions">
            <span>{correctionChanges.length} product{correctionChanges.length === 1 ? "" : "s"} changed</span>
            <span className="mc-detail__corrector">Correcting as <strong>{correctionActorName}</strong></span>
            <button className="mc-detail__btn mc-detail__btn--primary" type="button" disabled={!correctionChanges.length} onClick={() => setCorrectionSubmitOpen(true)}>Submit Corrections</button>
            <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={cancelCorrectionMode}>Cancel correction</button>
          </div>
        </section>
      ) : null}

      {correctionResult ? (
        <section className="mc-detail__correctionResult" role="status">
          <div>
            <div className="mc-detail__lockedEyebrow">Correction submitted</div>
            <h2>Version {correctionResult.version} recorded by {correctionResult.correctedBy}</h2>
            <p>{correctionResult.changedProducts.length} product{correctionResult.changedProducts.length === 1 ? "" : "s"} changed · {correctionResult.reason}</p>
          </div>
          <div className="mc-detail__correctionResultMetrics">
            <span><small>Original exposure</small><strong>{formatCurrency(correctionResult.originalExposure)}</strong></span>
            <span><small>Exposure movement</small><strong className="positive">{formatCurrency(Math.abs(correctionResult.recoveredValue))}</strong></span>
            <span><small>Current exposure</small><strong>{formatCurrency(correctionResult.correctedExposure)}</strong></span>
          </div>
        </section>
      ) : null}

      {completionResponse ? (
        <section className={`mc-detail__responseDesigned ${completionResponse.failed ? "has-warning" : "is-success"}`}>
          <div>
            <div className="mc-detail__responseDesignedEyebrow">Response designed</div>
            <h2>Count completed</h2>
            <p>
              {completionResponse.hadVariance
                ? `${completionResponse.flagged} flagged product${completionResponse.flagged === 1 ? "" : "s"} · ${formatCurrency(completionResponse.exposure)}`
                : "No variance detected. No alert was required."}
            </p>
          </div>
          <div className="mc-detail__responseDesignedStatus">
            {completionResponse.hadVariance ? <strong>Response created</strong> : <strong>Count verified</strong>}
            {completionResponse.emailQueued > 0 ? (
              <span>Email queued for {completionResponse.recipients?.map((item) => `${item.role} - ${item.name}`).join(", ") || `${completionResponse.emailQueued} recipient${completionResponse.emailQueued === 1 ? "" : "s"}`}. Delivery continues in the background.</span>
            ) : null}
            {completionResponse.delivered > 0 ? (
              <span>Email sent automatically to {completionResponse.recipients?.map((item) => `${item.role} - ${item.name}`).join(", ") || `${completionResponse.delivered} recipient${completionResponse.delivered === 1 ? "" : "s"}`}.</span>
            ) : null}
            {completionResponse.delivered > 0 && completionResponse.recipients?.length ? <span title={completionResponse.recipients.map((item) => `${item.name}: ${item.email}`).join("\n")}>{completionResponse.recipients.map((item) => item.email).join(", ")}</span> : null}
            {completionResponse.whatsappQueued > 0 ? (
              <span>WhatsApp queued for {completionResponse.whatsappRecipients?.map((item) => `${item.role} - ${item.name}`).join(", ") || `${completionResponse.whatsappQueued} recipient${completionResponse.whatsappQueued === 1 ? "" : "s"}`}. Delivery continues in the background.</span>
            ) : null}
            {completionResponse.whatsappSkipped > 0 ? <span>WhatsApp skipped — {completionResponse.whatsappSkipReason}</span> : null}
            {completionResponse.whatsappSent > 0 ? (
              <span>WhatsApp sent automatically to {completionResponse.whatsappRecipients?.map((item) => `${item.role} - ${item.name}`).join(", ")}.</span>
            ) : null}
            {completionResponse.whatsappPrepared > 0 ? (
              <span>WhatsApp alert prepared for {completionResponse.whatsappRecipients?.map((item) => `${item.role} - ${item.name}`).join(", ")} · test mode until Meta credentials are connected.</span>
            ) : null}
            {(completionResponse.whatsappSent > 0 || completionResponse.whatsappPrepared > 0) && completionResponse.whatsappRecipients?.length ? (
              <span title={completionResponse.whatsappRecipients.map((item) => `${item.name}: ${item.phone}`).join("\n")}>{completionResponse.whatsappRecipients.map((item) => item.phone).join(", ")}</span>
            ) : null}
            {completionResponse.failed > 0 ? <span>Email was not delivered. Check Notifications for the confirmed provider error.</span> : null}
            {completionResponse.whatsappFailed > 0 ? <span>WhatsApp was not prepared. Check Notifications for the gateway error.</span> : null}
            {completionResponse.hadVariance && completionResponse.emailQueued === 0 && completionResponse.delivered === 0 && completionResponse.failed === 0 ? <span>Email not sent — add a Site Manager, Operations, or Owner email under Sites.</span> : null}
          </div>
        </section>
      ) : null}

      {trafficProbeEnabled && !isArchivedView && trafficOpen ? (
        <section className="mc-detail__trafficPanel" aria-label="Development traffic inspector">
          <div className="mc-detail__trafficPanelHead">
            <div>
              <div className="mc-detail__trafficEyebrow">Development only · local measurement</div>
              <strong>Live Count Traffic</strong>
              <small>Measures the payloads on this tab's existing Firestore path. No probe network calls are added.</small>
            </div>
            <button
              className="mc-detail__btn mc-detail__btn--ghost mc-detail__btn--small"
              type="button"
              onClick={() => setTrafficStats(resetMcTrafficProbe(sessionId))}
            >
              Reset
            </button>
          </div>

          <div className="mc-detail__trafficGrid">
            <div><span>Edits</span><strong>{Number(trafficStats.edits || 0).toLocaleString()}</strong><small>digits / note changes</small></div>
            <div><span>Logical saves</span><strong>{Number(trafficStats.logicalSaves || 0).toLocaleString()}</strong><small>{formatTrafficBytes(trafficStats.logicalSaveBytes)} queued</small></div>
            <div><span>Write attempts</span><strong>{Number(trafficStats.writeAttempts || 0).toLocaleString()}</strong><small>A1 {Number(trafficStats.attempt1 || 0)} · A2 {Number(trafficStats.attempt2 || 0)} · A3 {Number(trafficStats.attempt3 || 0)}</small></div>
            <div><span>Committed writes</span><strong>{Number(trafficStats.firestoreWrites || 0).toLocaleString()}</strong><small>{Number(trafficStats.writeAmplification || 0).toFixed(2)}× writes / logical save</small></div>

            <div><span>Ack mismatches</span><strong>{Number(trafficStats.ackMismatches || 0).toLocaleString()}</strong><small>causes application retry</small></div>
            <div><span>Transaction failures</span><strong>{Number(trafficStats.transactionFailures || 0).toLocaleString()}</strong><small>write path errors</small></div>
            <div><span>Ack read failures</span><strong>{Number(trafficStats.ackReadFailures || 0).toLocaleString()}</strong><small>verification read errors</small></div>
            <div><span>Coalescing</span><strong>{Number(trafficStats.coalescingRatio || 0).toFixed(1)}×</strong><small>edits per logical save</small></div>

            <div><span>Listener · server</span><strong>{Number(trafficStats.listenerServerSnapshots || 0).toLocaleString()}</strong><small>{formatTrafficBytes(trafficStats.listenerServerBytes)} estimated docs</small></div>
            <div><span>Listener · cache</span><strong>{Number(trafficStats.listenerCacheSnapshots || 0).toLocaleString()}</strong><small>{formatTrafficBytes(trafficStats.listenerCacheBytes)} local-cache docs</small></div>
            <div><span>Listener · pending</span><strong>{Number(trafficStats.listenerPendingSnapshots || 0).toLocaleString()}</strong><small>{formatTrafficBytes(trafficStats.listenerPendingBytes)} local-write echoes</small></div>
            <div><span>All listener callbacks</span><strong>{Number(trafficStats.listenerSnapshots || 0).toLocaleString()}</strong><small>{formatTrafficBytes(trafficStats.listenerBytes)} raw value only</small></div>

            <div><span>Raw value path</span><strong>{formatTrafficBytes(trafficStats.totalObservedBytes)}</strong><small>legacy probe: value reads + writes</small></div>
            <div><span>Duplicated JSON writes</span><strong>{formatTrafficBytes(trafficStats.duplicatedJsonWriteBytes)}</strong><small>second copy stored beside value</small></div>
            <div><span>Server-path estimate</span><strong>{formatTrafficBytes(trafficStats.serverPathEstimatedDocumentBytes)}</strong><small>writes + tx/ack/direct + server listener</small></div>
            <div><span>All doc observations</span><strong>{formatTrafficBytes(trafficStats.totalEstimatedDocumentBytes)}</strong><small>includes cache/pending listener callbacks</small></div>

            <div><span>Estimated write docs</span><strong>{formatTrafficBytes(trafficStats.estimatedDocumentWriteBytes)}</strong><small>full stored field payload</small></div>
            <div><span>Estimated read docs</span><strong>{formatTrafficBytes(trafficStats.estimatedDocumentReadBytes)}</strong><small>full returned field payload</small></div>
            <div><span>Largest raw value</span><strong>{formatTrafficBytes(trafficStats.largestPayloadBytes)}</strong><small>serialized value field only</small></div>
            <div><span>Largest estimated doc</span><strong>{formatTrafficBytes(trafficStats.largestEstimatedDocumentBytes)}</strong><small>value + json + metadata fields</small></div>
          </div>

          {trafficKeyRows.length ? (
            <div className="mc-detail__trafficBreakdown">
              <div className="mc-detail__trafficBreakdownTitle">Per-key breakdown</div>
              <div className="mc-detail__trafficTableWrap">
                <table className="mc-detail__trafficTable">
                  <thead>
                    <tr>
                      <th>Authority</th>
                      <th>Saves</th>
                      <th>Attempts</th>
                      <th>Writes</th>
                      <th>Tx reads</th>
                      <th>Ack reads</th>
                      <th>Listener S/C/P</th>
                      <th>Raw value</th>
                      <th>Dup JSON</th>
                      <th>Est. docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trafficKeyRows.map((row) => (
                      <tr key={row.key}>
                        <td title={row.key}><strong>{trafficKeyLabel(row.key, sessionId)}</strong><small>{row.key}</small></td>
                        <td>{Number(row.logicalSaves || 0).toLocaleString()}</td>
                        <td>{Number(row.writeAttempts || 0).toLocaleString()}<small>A1 {Number(row.attempt1 || 0)} · A2 {Number(row.attempt2 || 0)} · A3 {Number(row.attempt3 || 0)}</small></td>
                        <td>{Number(row.firestoreWrites || 0).toLocaleString()}</td>
                        <td>{Number(row.transactionReads || 0).toLocaleString()}</td>
                        <td>{Number(row.ackReads || 0).toLocaleString()}<small>{Number(row.ackMismatches || 0)} mismatch</small></td>
                        <td>{Number(row.listenerServerSnapshots || 0)}/{Number(row.listenerCacheSnapshots || 0)}/{Number(row.listenerPendingSnapshots || 0)}</td>
                        <td>{formatTrafficBytes(Number(row.firestoreWriteBytes || 0) + Number(row.firestoreReadBytes || 0))}</td>
                        <td>{formatTrafficBytes(row.duplicatedJsonWriteBytes)}</td>
                        <td>{formatTrafficBytes(row.estimatedTotalDocumentBytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="mc-detail__trafficFoot">
            Read detail: {Number(trafficStats.directReads || 0)} direct · {Number(trafficStats.transactionReads || 0)} transaction · {Number(trafficStats.ackReads || 0)} acknowledgement · {Number(trafficStats.listenerSnapshots || 0)} listener.
            Listener split uses Firestore snapshot metadata: server = not cache and not pending; cache and pending may overlap.
            {Number(trafficStats.failedWrites || 0) > 0 ? ` Failed transaction/write attempts: ${Number(trafficStats.failedWrites || 0)}.` : ""}
            <span> Estimates measure document field payloads, not Firestore billing bytes; protocol, indexes, compression and cached listener behavior are not billed exactly like these figures.</span>
          </div>
        </section>
      ) : null}

      <section className="mc-detail__topline">
        <article className="mc-detail__metric"><span>Status</span><strong>{correctionMode ? "Correction Mode" : sectionCompleted ? "Section Complete" : isComplete ? "Ready to Complete" : meta.status}</strong></article>
        <article className="mc-detail__metric"><span>Progress</span><strong>{totals.completedLines} / {totals.totalLines}</strong></article>
        <article className="mc-detail__metric"><span>Flagged Items</span><strong>{totals.flaggedCount}</strong></article>
        <article className={`mc-detail__metric mc-detail__metric--${exposureClass}`}><span>Cost Exposure</span><strong>{formatCurrency(totals.totalExposure)}</strong></article>
      </section>

      <section className="mc-detail__signal">
        <div>
          <div className="mc-detail__signalLabel">Count status</div>
          <p>
            {isArchivedView ? "Archived sessions are view-only. Review the final count values, variance and notes below." : correctionMode ? "Correction Mode is active. Only changed products are tracked in this protected draft." : sectionCompleted ? "This count is locked. Review the completed values below or begin an audited correction." : "Imported count lines are live and ready. Enter physical counts below. Variance and cost exposure update automatically."}
          </p>
        </div>

        {completionError ? (
          <div className="mc-detail__completionError" role="alert">
            <strong>Completion not confirmed.</strong>
            <span>{completionError}</span>
          </div>
        ) : null}

        <div className="mc-detail__savePanel">
          <div className="mc-detail__savePanelTop">
            <div className={`mc-detail__saveState is-${saveState}`}>
              <span className="mc-detail__saveDot" />
              {saveState === "saving"
                ? `Saving ${Math.min(Number(saveProgress.savedCount || 0), Number(saveProgress.targetCount || totals.completedLines || 0))} / ${Number(saveProgress.targetCount || totals.completedLines || 0)} counted rows…`
                : "All changes saved"}
            </div>
            {!isArchivedView && (!sectionCompleted || correctionMode) ? (<button className="mc-detail__btn mc-detail__btn--ghost mc-detail__btn--small" type="button" onClick={saveNow}>
              {correctionMode ? "Save draft" : "Save now"}
            </button>) : null}
          </div>
          {saveState === "saving" ? (
            <div className="mc-detail__saveProgress" aria-live="polite">
              <div className="mc-detail__saveProgressTrack">
                <span
                  className="mc-detail__saveProgressFill"
                  style={{ width: `${Math.max(3, Math.min(100, (Number(saveProgress.savedCount || 0) / Math.max(1, Number(saveProgress.targetCount || totals.completedLines || 1))) * 100))}%` }}
                />
              </div>
              <small>Keep counting. Saving continues in the background if you leave this screen.</small>
            </div>
          ) : null}
          <span className="mc-detail__savedAt mc-detail__savedAt--under">{formatRelativeLastSaved(lastSavedAt)}</span>
        </div>
      </section>

      <section className="mc-detail__toolbar">
        <div className="mc-detail__searchWrap">
          <label className="mc-detail__toolbarLabel" htmlFor="mc-count-search">Search</label>
          <input
            id="mc-count-search"
            className="mc-detail__search"
            type="text"
            placeholder="Search product, category or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="mc-detail__filterWrap">
          <span className="mc-detail__toolbarLabel">Quick view</span>
          <div className="mc-detail__filterChips">
            {[
              ["all", "All"],
              ["uncounted", "Uncounted"],
              ["counted", "Counted"],
              ["variance", "Variance"],
              ["flagged", "Flagged"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`mc-detail__chip ${quickFilter === value ? "is-active" : ""}`}
                onClick={() => {
                  if (value !== "uncounted") setHeldUncountedIds(new Set());
                  setQuickFilter(value);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mc-detail__tableWrap">
        <div className="mc-detail__tableHead">
          <div>Product</div>
          <div>Category</div>
          <div>SKU</div>
          <div>System SOH</div>
          <div>Counted</div>
          <div>Waste</div>
          <div>Variance</div>
          <div>Cost Variance</div>
          <div>Notes</div>
        </div>

        <div className="mc-detail__rows">
          {groupedFilteredRows.map((group) => {
            const isCollapsed = collapsedGroups[group.name] ?? true;
            const countedInGroup = group.rows.filter((row) => row.touched).length;
            const exposureInGroup = roundTo2(group.rows.reduce((sum, row) => sum + Number(row.costVariance || 0), 0));
            return (
              <div className="mc-detail__categoryGroup" key={`group-${group.name}`}>
                <button className="mc-detail__categoryGroupHeader" type="button" onClick={() => toggleGroup(group.name)}>
                  <div>
                    <strong>{group.name}</strong>
                    <span>{countedInGroup} / {group.rows.length} counted</span>
                  </div>
                  <div className={`mc-detail__categoryGroupExposure ${exposureInGroup > 0 ? "positive" : exposureInGroup < 0 ? "negative" : ""}`}>
                    {formatCurrency(exposureInGroup)} <span>{isCollapsed ? "▾" : "▴"}</span>
                  </div>
                </button>

                {!isCollapsed ? group.rows.map((line) => (
                  <div
                    className={`mc-detail__row ${line.flagged ? `is-flagged is-flagged--${line.flagDirection || "note"}` : ""} ${line.touched ? "is-counted" : "is-pending"}`}
                    key={line.id}
                  >
                    <div className="mc-detail__productCell">
                      <div className="mc-detail__productName">{line.product}</div>
                      <div className="mc-detail__productMeta">
                        Imported {formatCurrency(line.sourceCost ?? line.cost)}{line.priceBasis === "base" ? ` per ${line.baseUnit || "Unit"}` : ` per ${formatNumber(line.priceUnitSize || 1)} ${line.baseUnit || "Unit"}`}
                        {Number(line.cost) !== Number(line.sourceCost ?? line.cost) ? ` · Effective ${formatCurrency(line.cost)} / ${line.baseUnit || "Unit"}` : ""}
                        {line.autoDetected ? " · Auto-detected" : ""}
                      </div>
                    </div>
                    <div className="mc-detail__metaCell">{line.category || "—"}</div>
                    <div className="mc-detail__metaCell">{line.sku || "—"}</div>
                    <div className="mc-detail__numberCell">{formatNumber(line.systemSoh)}</div>
                    <div><input ref={(node) => { fieldInputRefs.current.counted[line.id] = node; }} className="mc-detail__input" inputMode="decimal" autoComplete="off" type="text" value={line.counted} readOnly={isArchivedView || (sectionCompleted && !correctionMode)} disabled={isArchivedView || (sectionCompleted && !correctionMode)} tabIndex={isArchivedView || (sectionCompleted && !correctionMode) ? -1 : undefined} aria-disabled={isArchivedView || (sectionCompleted && !correctionMode)} onChange={(e) => updateLine(line.id, "counted", e.target.value)} onKeyDown={(e) => handleCountFieldKeyDown(e, line, "counted")} /></div>
                    <div><input ref={(node) => { fieldInputRefs.current.waste[line.id] = node; }} className="mc-detail__input" inputMode="decimal" autoComplete="off" type="text" value={line.waste} readOnly={isArchivedView || (sectionCompleted && !correctionMode)} disabled={isArchivedView || (sectionCompleted && !correctionMode)} tabIndex={isArchivedView || (sectionCompleted && !correctionMode) ? -1 : undefined} aria-disabled={isArchivedView || (sectionCompleted && !correctionMode)} onChange={(e) => updateLine(line.id, "waste", e.target.value)} onKeyDown={(e) => handleCountFieldKeyDown(e, line, "waste")} /></div>
                    <div className={`mc-detail__variance ${line.variance > 0 ? "positive" : line.variance < 0 ? "negative" : ""}`}>{line.touched ? formatNumber(line.variance) : "—"}</div>
                    <div className={`mc-detail__variance ${line.costVariance > 0 ? "positive" : line.costVariance < 0 ? "negative" : ""}`}>{line.touched ? formatCurrency(line.costVariance) : "—"}</div>
                    <div><input ref={(node) => { fieldInputRefs.current.note[line.id] = node; }} className="mc-detail__input mc-detail__input--note" type="text" placeholder="Add note or flag" value={line.note} readOnly={isArchivedView || (sectionCompleted && !correctionMode)} disabled={isArchivedView || (sectionCompleted && !correctionMode)} tabIndex={isArchivedView || (sectionCompleted && !correctionMode) ? -1 : undefined} aria-disabled={isArchivedView || (sectionCompleted && !correctionMode)} onChange={(e) => updateLine(line.id, "note", e.target.value)} onKeyDown={(e) => handleCountFieldKeyDown(e, line, "note")} /></div>
                  </div>
                )) : null}
              </div>
            );
          })}

          {!filteredRows.length ? (
            <div className="mc-detail__emptyState">
              No lines match this view yet. Try a different search or filter.
            </div>
          ) : null}
        </div>
      </section>


      {orderModalOpen ? (
        <div className="mc-order-modal">
          <div className="mc-order-modal__backdrop" onClick={() => setOrderModalOpen(false)} />
          <div className="mc-order-modal__panel">
            <aside className="mc-order-modal__history">
              <div className="mc-detail__eyebrow">Order history</div>
              <h3>Saved MOS</h3>
              <div className="mc-order-historyList">
                {visibleOrderSheets.length ? visibleOrderSheets.map((sheet) => (
                  <button key={sheet.id} type="button" className={`mc-order-historyItem ${selectedOrderSheet?.id === sheet.id ? "is-active" : ""}`} onClick={() => setSelectedOrderSheet(sheet)}>
                    <strong>{sheet.storeName}</strong>
                    <span>{sheet.sessionId}</span>
                    <small>Saved by {sheet.createdBy || "User"}</small>
                    <small>{sheet.createdAt ? new Date(sheet.createdAt).toLocaleString() : ""}</small>
                  </button>
                )) : <p className="mc-order-empty">No order sheets saved yet.</p>}
              </div>
              <div className="mc-order-historyActions">
                <button className="mc-detail__btn mc-detail__btn--ghost mc-detail__btn--small" type="button" onClick={() => { setSelectedOrderSheet(null); setOrderDraft(orderRowsFromCount()); }}>Create new order</button>
                <button className="mc-detail__btn mc-detail__btn--ghost mc-detail__btn--small" type="button" onClick={() => setOrderModalOpen(false)}>Cancel</button>
              </div>
            </aside>
            <main className="mc-order-modal__body">
              <div className="mc-order-modal__head">
                <div>
                  <div className="mc-detail__eyebrow">Morpheus Order Sheet</div>
                  <h2>{selectedOrderSheet ? "Saved order sheet" : "Create new order sheet"}</h2>
                  <p>{meta.store} • {meta.sessionId} • Started by {meta.startedBy}</p>
                  <p className="mc-session-category-line"><strong>Categories:</strong> {sessionCategoryScopeLabel(meta)}</p>
                </div>
                {selectedOrderSheet ? (
                  <button className="mc-detail__btn mc-detail__btn--primary" type="button" onClick={() => {
                    const exportRows = filterMosRowsForExport(selectedOrderSheet.rows || [], mosPdfConfig);
                    exportMorpheusOrderSheetPdf({ sheet: selectedOrderSheet, session: meta || {}, rows: exportRows, options: mosPdfConfig });
                  }}>Export PDF</button>
                ) : null}
              </div>
              <div className="mc-order-table">
                <div className="mc-order-table__head">
                  <div>Product</div><div>Category</div><div>SKU</div><div>Counted</div><div>Order</div><div>Notes</div>
                </div>
                {(selectedOrderSheet?.rows || orderDraft).map((row, index) => (
                  <div className="mc-order-table__row" key={row.id || index}>
                    <div>{row.product}</div>
                    <div>{row.category === '__unmapped__' ? 'Other' : row.category}</div>
                    <div>{row.sku}</div>
                    <div>{row.counted}</div>
                    <div>{selectedOrderSheet ? <span>{row.order || "0"}</span> : <input ref={(node) => { orderInputRefs.current[`order-${index}`] = node; }} className="mc-order-input mc-order-input--number" inputMode="decimal" value={row.order} onChange={(e) => setOrderDraft((current) => current.map((item, i) => i === index ? { ...item, order: e.target.value } : item))} onKeyDown={(e) => handleOrderKeyDown(e, index, "order")} />}</div>
                    <div>{selectedOrderSheet ? <span>{row.orderNote || "—"}</span> : <input ref={(node) => { orderInputRefs.current[`orderNote-${index}`] = node; }} className="mc-order-input" value={row.orderNote} onChange={(e) => setOrderDraft((current) => current.map((item, i) => i === index ? { ...item, orderNote: e.target.value } : item))} onKeyDown={(e) => handleOrderKeyDown(e, index, "orderNote")} />}</div>
                  </div>
                ))}
              </div>
              {!selectedOrderSheet ? (
                <div className="mc-order-actions">
                  <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={() => saveOrderSheet(false)}>Save order sheet</button>
                  <button className="mc-detail__btn mc-detail__btn--primary" type="button" onClick={() => saveOrderSheet(true)}>Save & Export PDF</button>
                </div>
              ) : null}
            </main>
          </div>
        </div>
      ) : null}

      {completionComparisonLoading ? (
        <div className="mc-detail-comparisonLoading" role="status" aria-live="assertive" aria-label="Loading comparison">
          <div className="mc-detail-comparisonLoading__backdrop" />
          <div className="mc-detail-comparisonLoading__content">
            <span className="mc-detail-comparisonLoading__spinner" aria-hidden="true" />
            <strong>Loading comparison…</strong>
            <small>Checking completed Sessions and finalized history.</small>
          </div>
        </div>
      ) : null}

      {completionComparison ? (
        <div className="mc-detail-confirm mc-detail-comparePrompt" role="dialog" aria-modal="true" aria-labelledby="mc-comparison-prompt-title">
          <button type="button" className="mc-detail-confirm__backdrop" aria-label="Close comparison prompt" onClick={() => setCompletionComparison(null)} />
          <div className="mc-detail-confirm__panel mc-detail-comparePrompt__panel">
            <div className="mc-detail-comparePrompt__eyebrow">Comparison intelligence</div>
            <h2 className="mc-detail-confirm__title" id="mc-comparison-prompt-title">This count moved from the previous {meta?.sectionName || (meta?.fullSite ? "Full Count" : "section")} count.</h2>
            <p className="mc-detail-confirm__copy">Morpheus first checks completed counts still in Sessions, then falls back to finalized history in Archives. Different cycles, dates and product scopes are allowed; the compare page uses only products counted in both sessions.</p>

            <div className="mc-detail-comparePrompt__movement">
              <div>
                <span>Previous exposure</span>
                <strong>{formatCurrency(completionComparison.previousExposure)}</strong>
                <small>{completionComparison.previousCycle} · {completionComparison.previousTime ? new Date(completionComparison.previousTime).toLocaleString() : "Previous count"}</small>
                <small>Counted by {completionComparison.previousActor}</small>
              </div>
              <div className="mc-detail-comparePrompt__arrow">→</div>
              <div>
                <span>Current exposure</span>
                <strong>{formatCurrency(completionComparison.currentExposure)}</strong>
                <small>{meta?.cycleName || "Current cycle"}</small>
                <small>{completionComparison.currentFlags} flagged product{completionComparison.currentFlags === 1 ? "" : "s"}</small>
              </div>
            </div>

            <div className={`mc-detail-comparePrompt__delta ${Math.abs(completionComparison.currentExposure) <= Math.abs(completionComparison.previousExposure) ? "is-improved" : "is-worse"}`}>
              <span>Exposure movement</span>
              <strong>{completionComparison.exposureDelta >= 0 ? "+" : ""}{formatCurrency(completionComparison.exposureDelta)}</strong>
              <small>{completionComparison.flagDelta === 0 ? "Flag count unchanged" : `${completionComparison.flagDelta > 0 ? "+" : ""}${completionComparison.flagDelta} flagged product${Math.abs(completionComparison.flagDelta) === 1 ? "" : "s"}`}</small>
            </div>

            <div className="mc-detail-confirm__actions">
              <button type="button" className="mc-detail-confirm__secondary" onClick={() => setCompletionComparison(null)}>Not now</button>
              <button type="button" className="mc-detail-confirm__primary" onClick={openCompletionComparison}>Compare counts</button>
            </div>
          </div>
        </div>
      ) : null}

      {correctionInfoOpen ? (
        <div className="mc-detail-confirm" role="dialog" aria-modal="true" aria-labelledby="mc-correction-info-title">
          <button type="button" className="mc-detail-confirm__backdrop" aria-label="Close correction dialog" onClick={() => setCorrectionInfoOpen(false)} />
          <div className="mc-detail-confirm__panel mc-detail-confirm__panel--correction">
            <div className="mc-detail__eyebrow">Review &amp; Correct Count</div>
            <h3 id="mc-correction-info-title" className="mc-detail-confirm__title">Why is this count being corrected?</h3>
            <p className="mc-detail-confirm__copy">The original completed count remains permanently protected. Select a reason before opening Correction Mode.</p>
            <div className="mc-correction-reasons">
              {["Product found", "Wrong quantity entered", "Transfer missed", "Supplier issue", "Stock adjustment", "Other"].map((reason) => (
                <label key={reason} className={`mc-correction-reason ${correctionReason === reason ? "is-selected" : ""}`}>
                  <input type="radio" name="mc-correction-reason" value={reason} checked={correctionReason === reason} onChange={(event) => setCorrectionReason(event.target.value)} />
                  <span>{reason}</span>
                </label>
              ))}
            </div>
            {correctionReason === "Other" ? (
              <label className="mc-correction-other">
                <span>Correction reason</span>
                <textarea value={correctionOtherReason} onChange={(event) => setCorrectionOtherReason(event.target.value)} placeholder="Explain why this completed count needs correction" rows={3} />
              </label>
            ) : null}
            <div className="mc-detail-confirm__assurance">
              <strong>Original count protected</strong>
              <span>Changes are stored separately as a correction draft. Nothing is submitted in this part yet.</span>
            </div>
            <div className="mc-detail-confirm__actions">
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={() => setCorrectionInfoOpen(false)}>Cancel</button>
              <button className="mc-detail-confirm__primary" type="button" disabled={!correctionReason || (correctionReason === "Other" && !correctionOtherReason.trim())} onClick={beginCorrectionMode}>Continue to Correction Mode</button>
            </div>
          </div>
        </div>
      ) : null}

      {correctionSubmitOpen ? (
        <div className="mc-detail-confirm" role="dialog" aria-modal="true" aria-labelledby="mc-correction-submit-title">
          <button type="button" className="mc-detail-confirm__backdrop" aria-label="Close correction submission" onClick={() => { if (!correctionSubmitting) setCorrectionSubmitOpen(false); }} />
          <div className="mc-detail-confirm__panel mc-detail-confirm__panel--correction">
            <div className="mc-detail__eyebrow">Submit audited correction</div>
            <h3 id="mc-correction-submit-title" className="mc-detail-confirm__title">Record {correctionChanges.length} corrected product{correctionChanges.length === 1 ? "" : "s"}?</h3>
            <p className="mc-detail-confirm__copy">The original completed count remains protected. This submission creates a new correction version attributed to the signed-in Morpheus profile.</p>
            <div className="mc-correction-audit">
              <span><small>Corrected by</small><strong>{correctionActorName}</strong><em>{correctionActorRole}</em></span>
              <span><small>Reason</small><strong>{correctionReason === "Other" ? correctionOtherReason : correctionReason}</strong></span>
              <span><small>Original exposure</small><strong>{formatCurrency(correctionOriginalSummary.exposure)}</strong></span>
              <span><small>Current exposure</small><strong>{formatCurrency(totals.totalExposure)}</strong></span>
              <span><small>Recovery value</small><strong className={correctionRecovery >= 0 ? "positive" : "negative"}>{correctionRecovery >= 0 ? "+" : ""}{formatCurrency(correctionRecovery)}</strong></span>
            </div>
            <div className="mc-detail-confirm__actions">
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" disabled={correctionSubmitting} onClick={() => setCorrectionSubmitOpen(false)}>Continue correcting</button>
              <button className="mc-detail-confirm__primary" type="button" disabled={correctionSubmitting || !correctionChanges.length} onClick={submitCorrections}>{correctionSubmitting ? "Submitting…" : "Submit Corrections"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmFinalize ? (
        <div className="mc-detail-confirm" role="dialog" aria-modal="true" aria-labelledby="mc-finalize-title">
          <button
            type="button"
            className="mc-detail-confirm__backdrop"
            aria-label="Close finalize dialog"
            onClick={() => { if (!finalizeBusy) setConfirmFinalize(false); }}
          />
          <div className="mc-detail-confirm__panel">
            <div className="mc-detail__eyebrow">Finalize completed count</div>
            <h3 id="mc-finalize-title" className="mc-detail-confirm__title">Finalize {meta.sessionId}?</h3>
            <p className="mc-detail-confirm__copy">
              This will remove the completed count from Sessions and move it to Archive under completed sessions.
            </p>
            {finalizeBusy ? <p className="mc-detail-confirm__status">Finalizing count…</p> : null}
            <div className="mc-detail-confirm__actions">
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={() => setConfirmFinalize(false)} disabled={finalizeBusy}>
                Cancel
              </button>
              <button
                className="mc-detail-confirm__primary"
                type="button"
                onClick={finalizeCurrentSession}
                disabled={!canFinalize || finalizeBusy}
                title={canFinalize ? "Finalize this completed count" : "This profile is not allowed to finalize counts"}
              >
                {finalizeBusy ? "Finalizing…" : "Finalize Count"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmArchive ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div
            onClick={() => { if (!archiveBusy) setConfirmArchive(false); }}
            style={{ position: "absolute", inset: 0, background: "rgba(2,6,23,.62)", backdropFilter: "blur(12px)" }}
          />
          <div
            style={{
              position: "relative",
              width: "min(520px, 92vw)",
              borderRadius: 28,
              padding: 26,
              border: "1px solid rgba(103,232,249,.16)",
              background: "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.99))",
              boxShadow: "0 30px 120px rgba(0,0,0,.55)",
            }}
          >
            <div className="mc-detail__eyebrow">Archive session</div>
            <h3 style={{ margin: "0 0 8px", color: "#f8fafc" }}>Archive {meta.sessionId}?</h3>
            <p style={{ margin: "0 0 18px", color: "#9ab2ca" }}>
              This will remove the live count from Sessions and move it to Archive.
            </p>
            {archiveBusy ? <p className="mc-detail-confirm__status">Archiving session…</p> : null}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button className="mc-detail__btn mc-detail__btn--ghost" type="button" onClick={() => setConfirmArchive(false)} disabled={archiveBusy}>
                Cancel
              </button>
              <button className="mc-detail__btn mc-detail__btn--primary" type="button" onClick={archiveCurrentSession} disabled={archiveBusy}>
                {archiveBusy ? "Archiving…" : "Archive Session"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
